import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import "./SprintDashboard.css";
import "./SharedTable.css";
import "./Sprints.css";
import TicketSidePanel from "./TicketPanel.js";
import { FiEdit2, FiTrash2, FiPlus, FiChevronDown, FiChevronRight, FiPaperclip, FiDownload, FiGitBranch, FiRefreshCw, FiSearch, FiFilter, FiShare2, FiX, FiUser, FiUsers, FiClock, FiSave, FiXCircle, FiFileText, FiGitMerge, FiGitCommit, FiCheck, FiZap, FiLink, FiUploadCloud, FiRotateCcw } from "react-icons/fi";
import { GoBug } from "react-icons/go";
import { formatDate } from "./Common Components/CommonFunction.js";
import FilterDropdown from "./Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "./Common Components/useCurrentProject";
import {
  Autocomplete, Button, CircularProgress, Menu, MenuItem, TextField, Modal, Box, Typography, IconButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DetailedTicketView from "./DetailedTicketView.js";
import LiveMovePanel from "./LiveMovePanel.js";
import TicketTestCasesTab from "./Quality/TicketTestCasesTab";
import TestCaseImportModal from "./Quality/TestCaseImportModal";
import { hasPerm as hasQualityPerm } from "./Quality/qualityUi";
import { qualityApi } from "./Quality/qualityApi";
import TitleTooltip from "./Common Components/TitleTooltip";
import CopyButton, { htmlToText } from "./Common Components/CopyButton";
import { sanitizeHtml, looksLikeHtml } from "./Common Components/sanitizeHtml";
import { buildHistoryEntries, filterEntries, mentionMatcher } from "./Common Components/commentHistory";
import AppTooltip from "./Common Components/AppTooltip";
import FileViewer from "./Common Components/FileViewer/FileViewer";
import FileThumb from "./Common Components/FileViewer/FileThumb";
import { displayName, downloadFile, fileMeta, fileType } from "./Common Components/FileViewer/fileTypes";
import { sprintExportApi } from "./sprintExportApi";
import { sprintApi } from "./sprintApi";
import { invalidateRequirementsCache } from "./Common Components/requirementsCache";
import SprintNotificationBell from "./SprintNotifications/SprintNotificationBell";
import { useSprintNotificationPoll } from "./SprintNotifications/useSprintNotificationPoll";
import { sprintNotificationsApi } from "./SprintNotifications/sprintNotificationsApi";
import { showNotificationToasts } from "./SprintNotifications/notificationUi";

const BUG_TYPES = ["Development", "Production", "Gap"];

// ── Bugs following their story to In QA / QA Pass / In UAT / UAT Pass / Production ──
// Statuses in pipeline order, by name (lower-case letters only); a bug only ever
// moves forward along it. Anything off the pipeline (Hold, Not a Bug, Client
// Confirm, Duplicate, ...) is never changed.
const statusKey = (name = "") => {
  const k = name.toLowerCase().replace(/[^a-z]/g, "");
  return k === "live" ? "production" : k;
};
const STATUS_PIPELINE = ["todo", "devinprogress", "devcompleted", "inqa", "qainprogress", "peer", "qapass", "inuat", "uatinprogress", "uatpass", "production"];
const CASCADE_TARGETS = ["inqa", "qapass", "inuat", "uatpass", "production"];
const NOT_STARTED = ["todo", "devinprogress"];
const FAILED = ["qafail", "uatfail", "smokefail"];

// Rows for the "move its bugs too?" popup — pick: "tick" (pre-selected), "warn"
// (offered but unticked: failed / not started) or "lock" (left alone). Null when
// none of the bugs could follow.
const planBugCascade = (bugs, targetName) => {
  const target = statusKey(targetName);
  if (!CASCADE_TARGETS.includes(target) || !bugs?.length) return null;
  const targetIdx = STATUS_PIPELINE.indexOf(target);
  const rows = bugs.map(bug => {
    const name = bug.status_name || bug.ticket_status || "";
    const key = statusKey(name);
    const idx = STATUS_PIPELINE.indexOf(key);
    if (FAILED.includes(key)) return { bug, name, pick: "warn", reason: "failed" };
    if (idx === -1) return { bug, name, pick: "lock", reason: "never changed" };
    if (idx >= targetIdx) return { bug, name, pick: "lock", reason: "already there or ahead" };
    if (NOT_STARTED.includes(key)) return { bug, name, pick: "warn", reason: "not fixed yet" };
    return { bug, name, pick: "tick" };
  });
  return rows.some(r => r.pick !== "lock") ? rows : null;
};

// Mirrors ALLOWED_EXTENSIONS / MAX_FILE_SIZE_MB in MINIMI-BE ticketing/views.py.
const ATTACHMENT_EXTENSIONS = ["pdf", "png", "jpg", "jpeg", "docx", "xlsx", "csv"];
const ATTACHMENT_MAX_MB = 10;
const fileExt = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const SPRINT_STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Ongoing", value: "ONGOING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Cancelled", value: "CANCELLED" },
];

const STATUS_PILL_CLASS = {
  ONGOING: "sprint-status-pill--ongoing",
  COMPLETED: "sprint-status-pill--completed",
  OVERDUE: "sprint-status-pill--overdue",
  CANCELLED: "sprint-status-pill--cancelled",
};

/* ── Donut Chart (pure CSS conic-gradient) ── */
const DonutChart = ({ segments, total }) => {
  let gradient = "";
  let offset = 0;
  const colors = { green: "var(--status-green)", blue: "var(--status-blue)", yellow: "#f5a623", red: "var(--status-red)", gray: "var(--text-subtle)" };

  if (total === 0) {
    gradient = `${colors.gray} 0deg 360deg`;
  } else {
    const parts = [];
    segments.forEach(({ count, color }) => {
      if (count > 0) {
        const deg = (count / total) * 360;
        parts.push(`${colors[color] || colors.gray} ${offset}deg ${offset + deg}deg`);
        offset += deg;
      }
    });
    if (offset < 360) parts.push(`var(--bg-tertiary) ${offset}deg 360deg`);
    gradient = parts.join(", ");
  }

  return (
    <div className="sprint-donut" style={{ background: `conic-gradient(${gradient})` }}>
      <div className="sprint-donut__center">
        <span className="sprint-donut__number">{total}</span>
        <span className="sprint-donut__label">tickets</span>
      </div>
    </div>
  );
};

/* ── KPI Bar ── */
const KPIBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="sprint-kpi-bar-row">
      <span className="sprint-kpi-bar-label">{label}</span>
      <div className="sprint-kpi-bar-track">
        <div className={`sprint-kpi-bar-fill sprint-kpi-bar-fill--${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="sprint-kpi-bar-count">{count}</span>
    </div>
  );
};

/* ── Due date ── */
/**
 * Classify a due date relative to today: 'overdue' (past), 'soon' (today or
 * tomorrow), or 'normal'. Compared at day precision so a date due later today
 * never reads as overdue.
 */
const dueState = (raw) => {
  if (!raw) return null;
  const due = new Date(raw);
  if (Number.isNaN(due.getTime())) return null;
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return 'overdue';
  if (days <= 1) return 'soon';
  return 'normal';
};

const DueDateCell = ({ value }) => {
  const state = dueState(value);
  if (!state) return <span className="sl2-no-assign">-</span>;
  const label = new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  const title = state === 'overdue' ? 'Overdue' : state === 'soon' ? 'Due today / tomorrow' : '';
  return <AppTooltip title={title}><span className={`sl2-due sl2-due--${state}`}>{label}</span></AppTooltip>;
};

/* ═══════════════ MAIN COMPONENT ═══════════════ */
const SprintDashboard = () => {
  const userPermission = JSON.parse(localStorage.getItem("userPermissions") || "[]");

  // ── Core state ──
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useCurrentProject();
  const [sprintStatusFilter, setSprintStatusFilter] = useState("ONGOING");
  const [kpiData, setKpiData] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  // ── Level 2: Selected sprint + tickets ──
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [ticketList, setTicketList] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [filterQA, setFilterQA] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dueSort, setDueSort] = useState(""); // '' | 'asc' | 'desc'
  const [showFilters, setShowFilters] = useState(true);
  const [expandedParents, setExpandedParents] = useState({});
  const [bugCount, setBugCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);

  // ── Level 3: Selected ticket workspace ──
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketComments, setTicketComments] = useState([]);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [uploadingBatches, setUploadingBatches] = useState([]); // [{ id, names }] uploads in flight
  const [attDragOver, setAttDragOver] = useState(false);
  const [attFilter, setAttFilter] = useState("all"); // all | images | docs
  const [fileViewer, setFileViewer] = useState(null); // { files, index } — FileViewer's input
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");
  const fileInputRef = React.useRef(null);
  const [skippedFiles, setSkippedFiles] = useState([]); // [{ name, reason }] from the last pick
  const [ticketBranches, setTicketBranches] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [branchSyncing, setBranchSyncing] = useState(null);
  const [detailTab, setDetailTab] = useState('Details');
  // Quality module: badge count for the ticket's Test Cases tab.
  const [testCaseCount, setTestCaseCount] = useState(0);
  const qualityEnabled = hasQualityPerm('Quality');
  // Bulk import + link test cases for the open sprint (sheet with a Ticket column).
  const canImportCases = qualityEnabled && hasQualityPerm('Manage Test Cases');
  const [importingCases, setImportingCases] = useState(false);
  // Load the count when a ticket opens, so the badge is right before the tab is visited.
  useEffect(() => {
    setTestCaseCount(0);
    if (!qualityEnabled || !selectedTicket?.id) return undefined;
    let alive = true;
    qualityApi.getTicketTestCases(selectedTicket.id)
      .then(res => { if (alive) setTestCaseCount((res.data || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [qualityEnabled, selectedTicket?.id]);
  const [parentTicketStack, setParentTicketStack] = useState([]);
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [addBranchForm, setAddBranchForm] = useState({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' });
  const [gitlabRepos, setGitlabRepos] = useState([]);

  // ── Side panel (existing edit flow) ──
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // ── Sprint CRUD ──
  const [showSprintPopup, setShowSprintPopup] = useState(false);
  const [sprintForm, setSprintForm] = useState({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], is_live_support: false });
  const [editSprintId, setEditSprintId] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Live Support Sprint feature flag (Django admin → Configuration.enable_live_support_sprint)
  const [liveSupportEnabled, setLiveSupportEnabled] = useState(
    JSON.parse(localStorage.getItem("liveSupportEnabled") || "false")
  );

  // ── Context menu ──
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTicket, setMenuTicket] = useState(null);

  // ── Detailed view modal ──
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [detailViewTicket, setDetailViewTicket] = useState(null);

  // ── Inline edit state ──
  const [editRowId, setEditRowId] = useState(null);
  const [editedValues, setEditedValues] = useState({ short_description: "", ticket_status: "", assigned_to_name: "" });
  const [ticketStatuses, setTicketStatuses] = useState([]);

  // ── Move to sprint ──
  const [movePopup, setMovePopup] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveSaving, setMoveSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Story → a cascade status (In QA ... Production): which of its bugs follow it.
  const [cascadePopup, setCascadePopup] = useState(null); // { ticket, statusId, statusName, rows, save }
  const [cascadeChecked, setCascadeChecked] = useState([]);
  const [cascadeSaving, setCascadeSaving] = useState(false);
  // Bugs tab multi-select → one status for all of them.
  const [selectedBugIds, setSelectedBugIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null); // { bugs, statusId }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bugTypeMenu, setBugTypeMenu] = useState(null); // { ticket, top, left }
  const [allSprints, setAllSprints] = useState([]);

  // ── Add child/bug ticket ──
  const [addTicketPopup, setAddTicketPopup] = useState(false);
  const [addTicketParent, setAddTicketParent] = useState(null);
  const [addTicketType, setAddTicketType] = useState(""); // 'Story' or 'Bug'
  const [addTicketForm, setAddTicketForm] = useState({ short_description: "", status_id: "", priority_id: "", bug_type: "Development" });
  const [priorityTypes, setPriorityTypes] = useState([]);

  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // ── Level 1: Per-sprint developer performance ──
  const [sprintDevData, setSprintDevData] = useState({});

  // ── Inline status edit (Level 2 table) ──
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDropPos, setStatusDropPos] = useState({ top: 0, left: 0 });

  const openStatusDrop = (e, ticketId) => {
    e.stopPropagation();
    if (editingStatusId === ticketId) { setEditingStatusId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < 260 ? rect.top - Math.min(260, rect.top - 10) : rect.bottom + 4;
    setStatusDropPos({ top, left: rect.left });
    setBugTypeMenu(null);
    setEditingStatusId(ticketId);
  };

  // Close status dropdown on outside click
  useEffect(() => {
    if (!editingStatusId) return;
    const handler = () => setEditingStatusId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [editingStatusId]);

  // Bug selection belongs to the open story; start fresh on another ticket.
  const selectedTicketId = selectedTicket?.id;
  useEffect(() => { setSelectedBugIds([]); }, [selectedTicketId]);

  // ── Quick Create Ticket (Level 2) ──
  // Direct parent ticket creation inside a sprint is blocked — parent
  // Epic/Story points must be moved in from Client Requirements.

  // ── Inline editing (Level 3) ──
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editedTicket, setEditedTicket] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [expandedBranchId, setExpandedBranchId] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);

  // ── Move back to Client Requirements (Level 3) ──
  const [moveBackTicket, setMoveBackTicket] = useState(null); // ticket obj or null
  const [moveBackClientId, setMoveBackClientId] = useState('');
  const [moveBackStatusId, setMoveBackStatusId] = useState('');
  const [moveBackSaving, setMoveBackSaving] = useState(false);
  const [crClients, setCrClients] = useState([]);
  const [crStatuses, setCrStatuses] = useState([]);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` };

  // Opens the move-back dialog. Unmapped tickets need a Client (and optional
  // status) for the new requirement, so load those lists lazily.
  const openMoveBack = async (ticket) => {
    setMoveBackTicket(ticket);
    setMoveBackClientId('');
    setMoveBackStatusId('');
    if (!ticket.client_requirement_id) {
      try {
        const [cRes, sRes] = await Promise.all([
          fetch(`${API_BASE_URL}client_requirements/client/`, { headers }),
          fetch(`${API_BASE_URL}client_requirements/clientstatus/`, { headers }),
        ]);
        const cData = await cRes.json();
        const sData = await sRes.json();
        if (cData.status === 'success') setCrClients(cData.data || []);
        if (sData.status === 'success') setCrStatuses(sData.data || []);
      } catch (e) { console.error(e); }
    }
  };

  const handleMoveBack = async () => {
    const ticket = moveBackTicket;
    if (!ticket) return;
    if (!ticket.client_requirement_id && !moveBackClientId) {
      showErrorToast('Please select a client for the new requirement');
      return;
    }
    setMoveBackSaving(true);
    try {
      // Mapped ticket → revoke the sprint move (ticket detached + reused later).
      // Unmapped ticket → create a new Client Requirement from the ticket.
      const res = ticket.client_requirement_id
        ? await fetch(`${API_BASE_URL}client_requirements/requirements/revoke-sprint/`, {
            method: 'POST', headers,
            body: JSON.stringify({ id: ticket.client_requirement_id }),
          })
        : await fetch(`${API_BASE_URL}client_requirements/requirements/from-ticket/`, {
            method: 'POST', headers,
            body: JSON.stringify({
              ticket_id: ticket.id,
              client_id: moveBackClientId,
              ...(moveBackStatusId ? { status_id: moveBackStatusId } : {}),
            }),
          });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast(data.message || 'Moved back to Client Requirements');
        setMoveBackTicket(null);
        setSelectedTicket(null);
        setRefreshTrigger(p => !p);
      } else {
        showErrorToast(data.message || 'Move back failed');
      }
    } catch (e) { console.error(e); showErrorToast('Move back failed'); }
    finally { setMoveBackSaving(false); }
  };

  // Fetch the company feature config so the Live Support Sprint checkbox shows
  // only when enabled in Django admin.
  useEffect(() => {
    fetch(`${API_BASE_URL}core/feature-config/`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const enabled = Boolean(data.data?.live_support_sprint);
          setLiveSupportEnabled(enabled);
          localStorage.setItem("liveSupportEnabled", JSON.stringify(enabled));
        }
      })
      .catch((err) => console.error("Failed to load feature config:", err));
  }, []);

  // ══════════ DATA FETCHING ══════════

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/project/`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setProjects(data.data);
        setCurrentProject(prev => resolveProject(data.data, prev));
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchKPI = useCallback(async (projectId, statusFilter) => {
    if (!projectId) return;
    setKpiLoading(true);
    try {
      const statusParam = statusFilter === "all" ? "" : `&status=${statusFilter}`;
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/kpi/?project_id=${projectId}${statusParam}`, { headers });
      const data = await res.json();
      if (data.status === "success") setKpiData(data.data);
      else showErrorToast(data.message);
    } catch (e) { console.error(e); showErrorToast("Failed to load sprints"); }
    finally { setKpiLoading(false); }
  }, []);

  const fetchTickets = useCallback(async (sprintId) => {
    if (!sprintId) return;
    setTicketLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${sprintId}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        const tickets = Array.isArray(data.data) ? data.data : [];
        setTicketList(tickets);
        setBugCount(data.bug_counts || 0);
        setTicketCount(data.total_tickets || 0);
        // Helper to find a ticket by id in the refreshed data
        const findTicketById = (id) => {
          for (const t of tickets) {
            if (t.id === id) return t;
            if (t.child_tickets) {
              for (const c of t.child_tickets) {
                if (c.id === id) return c;
                if (c.bug_tickets) {
                  for (const b of c.bug_tickets) { if (b.id === id) return b; }
                }
              }
            }
            if (t.bug_tickets) {
              for (const b of t.bug_tickets) { if (b.id === id) return b; }
            }
          }
          return null;
        };
        // Re-sync selectedTicket with fresh data
        setSelectedTicket(prev => {
          if (!prev) return prev;
          return findTicketById(prev.id) || prev;
        });
        // Re-sync parentTicketStack with fresh data
        setParentTicketStack(prev => prev.map(entry => {
          const fresh = findTicketById(entry.ticket.id);
          return fresh ? { ...entry, ticket: fresh } : entry;
        }));
      } else showErrorToast(data.message);
    } catch (e) { console.error(e); }
    finally { setTicketLoading(false); }
  }, []);

  const fetchComments = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setCommentsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/comments/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketComments(data.data || []);
    } catch (e) { console.error(e); }
    finally { setCommentsLoading(false); }
  }, []);

  const fetchBranches = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketBranches(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchGitlabRepos = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/repos/?project_id=${projectId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setGitlabRepos(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAttachments = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketAttachments(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/employee/`, { headers });
      const data = await res.json();
      if (data.status === "success") setEmployees(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTicketStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/status/`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketStatuses(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchPriorityTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/priorityType/`, { headers });
      const data = await res.json();
      if (data.status === "success") setPriorityTypes(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTicketTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/ticketType/`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketTypes(data.data);
    } catch (e) { console.error(e); }
  }, []);

  // ── Move-to-Production guard ───────────────────────────────────────────
  // A ticket can only be moved to the "Production" status after its move-to-live
  // request is approved. We block it on the client (with an alert) so the user
  // gets immediate feedback; the backend enforces the same rule authoritatively.
  const isProductionStatus = (statusId) => {
    const s = ticketStatuses.find((st) => String(st.id) === String(statusId));
    const name = (s?.ticket_status || '').trim();
    return /production/i.test(name) || /^live$/i.test(name);
  };

  const liveMoveApproved = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/live-move/?ticket_id=${ticketId}`, {
        method: 'GET', headers,
      });
      const data = await res.json();
      return data?.data?.latest?.request_status === 'APPROVED';
    } catch {
      return false;
    }
  };

  const ensureStatusAllowed = async (ticketId, newStatusId) => {
    const featureOn =
      JSON.parse(localStorage.getItem('releaseApprovalEnabled') || 'false') ||
      JSON.parse(localStorage.getItem('liveSupportEnabled') || 'false');
    if (!featureOn || !isProductionStatus(newStatusId)) return true;
    const approved = await liveMoveApproved(ticketId);
    if (!approved) {
      showErrorToast('This ticket cannot be moved to Production until its move-to-live request is approved.');
      return false;
    }
    return true;
  };

  const statusNameOf = (statusId) => ticketStatuses.find(s => String(s.id) === String(statusId))?.ticket_status || '';

  // A story saved into a cascade status (In QA ... Production) first asks which of its
  // bugs follow it; save(skipBugCascade) does the story's own PUT and returns
  // whether it succeeded.
  const saveWithBugCascade = async (ticket, prevStatusId, newStatusId, save) => {
    const changed = String(prevStatusId || '') !== String(newStatusId || '');
    const rows = changed && ticket.ticket_type !== 'Bug' ? planBugCascade(ticket.bug_tickets, statusNameOf(newStatusId)) : null;
    if (!rows) return save(false);
    setCascadeChecked(rows.filter(r => r.pick === 'tick').map(r => r.bug.id));
    setCascadePopup({ ticket, statusId: newStatusId, statusName: statusNameOf(newStatusId), rows, save });
  };

  const confirmCascade = async (withBugs) => {
    if (!cascadePopup || cascadeSaving) return;
    const { ticket, statusId, statusName, save } = cascadePopup;
    setCascadeSaving(true);
    try {
      if (!(await save(true))) return;
      const ids = withBugs ? cascadeChecked : [];
      if (ids.length) {
        const data = await sprintApi.bulkBugStatus(ids, statusId, ticket.id);
        if (data.status === 'success') showSuccessToast(`${data.data?.updated ?? ids.length} bug(s) moved to ${statusName} with the story`);
        else showErrorToast(data.message || 'Story updated, but its bugs could not be updated');
        setRefreshTrigger(p => !p);
      }
      setCascadePopup(null);
    } catch (e) { showErrorToast('Bug status update failed'); }
    finally { setCascadeSaving(false); }
  };

  const handleInlineSave = async () => {
    if (!editedTicket) return;
    const newStatusId = editedTicket.status_id || editedTicket.status;
    if (!(await ensureStatusAllowed(editedTicket.id, newStatusId))) return;
    const save = async (skipBugCascade) => {
      setEditSaving(true);
      try {
        const payload = {
          id: editedTicket.id,
          short_description: editedTicket.short_description,
          detailed_description: editedTicket.detailed_description || '',
          user_story: editedTicket.user_story || '',
          acceptance_criteria: editedTicket.acceptance_criteria || '',
          status_id: editedTicket.status_id || editedTicket.status,
          priority_id: editedTicket.priority_id || editedTicket.priority,
          ticket_type: editedTicket.ticket_type,
          assigned_to_id: editedTicket.assignee_id || editedTicket.assignee || null,
          sprint_id: editedTicket.sprint_id,
          company_id: 1,
          developers: (editedTicket.developers || []).map(d => ({ id: d.id, name: d.name || d })),
          qa_id: editedTicket.qa_id || editedTicket.qa || null,
          peer_qa_id: editedTicket.peer_qa_id || editedTicket.peer_qa || null,
          uat_id: editedTicket.uat_id || editedTicket.uat || null,
          dev_est_time: editedTicket.dev_est_time || '',
          qa_est_time: editedTicket.qa_est_time || '',
          story_points: editedTicket.story_points || '',
          // Always sent (even empty) so clearing the date actually unsets it.
          due_date: editedTicket.due_date || '',
          ...(skipBugCascade ? { skip_bug_cascade: true } : {}),
        };
        const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
          method: 'PUT', headers, body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.status === 'success') {
          showSuccessToast('Ticket updated');
          invalidateRequirementsCache(); // a move to Production can update its Client Requirement
          setIsInlineEditing(false);
          setEditedTicket(null);
          setRefreshTrigger(p => !p);
          return true;
        }
        showErrorToast(data.message || 'Update failed');
        return false;
      } catch (e) { console.error(e); showErrorToast('Update failed'); return false; }
      finally { setEditSaving(false); }
    };
    await saveWithBugCascade(selectedTicket || editedTicket, selectedTicket?.status_id || selectedTicket?.status, newStatusId, save);
  };

  const handleInlineStatusChange = async (ticket, newStatusId) => {
    setEditingStatusId(null);
    if (!(await ensureStatusAllowed(ticket.id, newStatusId))) return;
    const save = async (skipBugCascade) => {
      try {
        const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
          method: 'PUT', headers,
          // Bug and story rows from a ticket's tabs may not carry sprint_id; the
          // update endpoint requires it, so fall back to the sprint on screen.
          body: JSON.stringify({
            id: ticket.id, sprint_id: ticket.sprint_id || selectedSprint?.id, status_id: newStatusId, company_id: 1,
            ...(skipBugCascade ? { skip_bug_cascade: true } : {}),
          }),
        });
        const data = await res.json();
        if (data.status === 'success') {
          showSuccessToast('Status updated');
          invalidateRequirementsCache();
          setRefreshTrigger(p => !p);
          return true;
        }
        showErrorToast(data.message || 'Update failed');
        return false;
      } catch (e) { showErrorToast('Status update failed'); return false; }
    };
    await saveWithBugCascade(ticket, ticket.status_id || ticket.status, newStatusId, save);
  };

  // Bugs tab: one status for every selected bug.
  const handleBulkBugStatus = async () => {
    if (!bulkStatus?.statusId || bulkSaving) return;
    setBulkSaving(true);
    try {
      const data = await sprintApi.bulkBugStatus(bulkStatus.bugs.map(b => b.id), bulkStatus.statusId);
      if (data.status === 'success') {
        showSuccessToast(data.message || 'Bugs updated');
        setBulkStatus(null);
        setSelectedBugIds([]);
        setRefreshTrigger(p => !p);
      } else showErrorToast(data.message || 'Update failed');
    } catch (e) { showErrorToast('Bug status update failed'); }
    finally { setBulkSaving(false); }
  };

  // Bug type stays editable after creation (chip in the bug header and Bugs tab).
  const handleBugTypeChange = async (ticket, bugType) => {
    setBugTypeMenu(null);
    if (!ticket || bugType === ticket.bug_type) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
        method: 'PUT', headers,
        body: JSON.stringify({ id: ticket.id, sprint_id: ticket.sprint_id || selectedSprint?.id, bug_type: bugType, company_id: 1 }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast('Bug type updated');
        setRefreshTrigger(p => !p);
      } else showErrorToast(data.message || 'Update failed');
    } catch (e) { showErrorToast('Bug type update failed'); }
  };

  // Same dropdown look and placement as the inline status dropdown.
  const openBugTypeMenu = (e, ticket) => {
    e.stopPropagation();
    if (bugTypeMenu?.ticket?.id === ticket.id) { setBugTypeMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const top = window.innerHeight - rect.bottom < 140 ? rect.top - 120 : rect.bottom + 4;
    setEditingStatusId(null);
    setBugTypeMenu({ ticket, top, left: rect.left });
  };

  // Close bug type dropdown on outside click
  useEffect(() => {
    if (!bugTypeMenu) return;
    const handler = () => setBugTypeMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [bugTypeMenu]);

  const fetchAllSprints = useCallback(async (projectId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${projectId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setAllSprints(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  // ══════════ EFFECTS ══════════

  useEffect(() => { fetchProjects(); fetchEmployees(); fetchTicketStatuses(); fetchPriorityTypes(); fetchTicketTypes(); }, []);
  useEffect(() => { if (currentProject) { fetchKPI(currentProject, sprintStatusFilter); fetchAllSprints(currentProject); } }, [currentProject, sprintStatusFilter, refreshTrigger]);
  useEffect(() => { if (selectedSprint) fetchTickets(selectedSprint.id); }, [selectedSprint, refreshTrigger]);
  useEffect(() => {
    if (selectedTicket) { fetchComments(selectedTicket.id); fetchAttachments(selectedTicket.id); fetchBranches(selectedTicket.id); }
    else { setTicketComments([]); setTicketAttachments([]); setTicketBranches([]); }
  }, [selectedTicket]);
  // Unsent file picks and link drafts belong to the ticket/tab they were made on;
  // left over, they showed (and would upload) on whichever ticket was opened next.
  useEffect(() => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setLinkUrl(""); setLinkName(""); setShowAddLink(false);
    setFileViewer(null);
    setSkippedFiles([]);
    setAttDragOver(false);
  }, [selectedTicket?.id, detailTab]);
  // Ctrl+V on the Attachments tab uploads pasted files / screenshots — unless the
  // user is pasting into a text field (e.g. the link form).
  const uploadFilesRef = React.useRef(null);
  useEffect(() => {
    if (!selectedTicketId || detailTab !== 'Attachments') return undefined;
    const onPaste = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.length) return;
      e.preventDefault();
      uploadFilesRef.current?.(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [selectedTicketId, detailTab]);
  useEffect(() => { if (currentProject) fetchGitlabRepos(currentProject); }, [currentProject]);

  // Fetch dev analysis for all visible sprints (Level 1 performance)
  useEffect(() => {
    if (!kpiData || kpiData.length === 0) return;
    const fetchDevForSprints = async () => {
      const results = {};
      await Promise.allSettled(kpiData.map(async (sp) => {
        try {
          const res = await fetch(`${API_BASE_URL}reports/sprint/analysis/?sprint_id=${sp.id}`, { method: 'GET', headers });
          const data = await res.json();
          if (data.status === 'success' && Array.isArray(data.data)) {
            const devMap = {};
            // Story + Epic only — bugs are surfaced via the separate Bugs KPI
            data.data.filter(t => t.ticket_type !== 'Bug').forEach(t => {
              const devName = t.developer_names || '';
              const name = typeof devName === 'string' ? devName.split(',')[0]?.trim() : '';
              if (!name) return;
              if (!devMap[name]) devMap[name] = { tickets: 0, done: 0, inProg: 0, bugs: 0, devHrs: 0 };
              devMap[name].tickets += 1;
              devMap[name].devHrs += parseFloat(t.dev_time_took || t.dev_time_taken || 0);
              const st = (t.ticket_status || '').toLowerCase();
              if (/done|completed|live|production/.test(st)) devMap[name].done += 1;
              else if (/progress|dev|qa|review/.test(st)) devMap[name].inProg += 1;
            });
            const arr = Object.entries(devMap).map(([n, s]) => {
              const completionRate = s.tickets > 0 ? Math.round((s.done / s.tickets) * 100) : 0;
              const score = Math.min(100, Math.max(0, Math.round(completionRate * 0.5 + (s.inProg > 0 ? 20 : s.done > 0 ? 20 : 0) + Math.max(0, 30 - s.bugs * 10))));
              return { name: n, ...s, completionRate, score };
            }).sort((a, b) => b.score - a.score);
            results[sp.id] = arr;
          }
        } catch { /* ignore */ }
      }));
      setSprintDevData(results);
    };
    fetchDevForSprints();
  }, [kpiData]);

  // ══════════ HANDLERS ══════════

  // ── Deep link: /sprints?sprint=<id>&ticket=<id> ──
  // Used by Quality to open a production bug's fix ticket. Loads the sprint
  // (switching project if it lives in another one), then opens the ticket
  // once that sprint's tickets have arrived. The params are cleared after so
  // the back button and refreshes behave normally.
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingTicketId, setPendingTicketId] = useState(null);

  useEffect(() => {
    const sprintParam = searchParams.get('sprint');
    if (!sprintParam) return;
    const ticketParam = searchParams.get('ticket');
    setSearchParams({}, { replace: true });
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}ticketing/sprint/?id=${sprintParam}`, { headers });
        const data = await res.json();
        const sprint = data.status === 'success' && data.data && !Array.isArray(data.data) ? data.data : null;
        if (!sprint) { showErrorToast('That sprint could not be found.'); return; }
        if (sprint.project_id && String(sprint.project_id) !== String(currentProject)) setCurrentProject(sprint.project_id);
        setSelectedSprint(sprint);
        setSelectedTicket(null);
        setTicketSearch("");
        setPendingTicketId(ticketParam ? { ticket: Number(ticketParam), sprint: sprint.id } : null);
      } catch (e) { showErrorToast('Could not open the sprint.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!pendingTicketId || !ticketList.length) return;
    // Wait for the requested sprint's tickets, not the previous sprint's.
    if (!ticketList.some(t => String(t.sprint_id) === String(pendingTicketId.sprint))) return;
    let found = null;
    const walk = (list) => list.some(t => {
      if (t.id === pendingTicketId.ticket) { found = t; return true; }
      return walk([...(t.child_tickets || []), ...(t.bug_tickets || [])]);
    });
    walk(ticketList);
    setPendingTicketId(null);
    if (found) { setSelectedTicket(found); setDetailTab('Details'); setParentTicketStack([]); }
    else showErrorToast('That ticket is not in this sprint any more.');
  }, [ticketList, pendingTicketId]);

  // Status notifications for the open sprint: the bell count is polled every
  // 60 s (also while a ticket is open) and newly arrived ones pop up as toasts.
  // A toast keeps the handler from when it appeared, so read the open sprint from a ref.
  const openSprintId = useRef(null);
  openSprintId.current = selectedSprint?.id;
  const openNotification = (item) => {
    if (!item.is_read) sprintNotificationsApi.markRead(item.id).then(() => notifications.refresh()).catch(() => {});
    if (String(item.sprint_id) === String(openSprintId.current)) {
      // The notification means the ticket changed since this list was loaded,
      // so reload the sprint's tickets first and open it from the fresh list.
      fetchTickets(item.sprint_id).then(() => {
        if (String(item.sprint_id) === String(openSprintId.current)) {
          setPendingTicketId({ ticket: item.ticket_id, sprint: item.sprint_id });
        }
      });
    } else {
      // The user moved to another sprint since the toast appeared: go back through the deep link.
      setSearchParams({ sprint: String(item.sprint_id), ticket: String(item.ticket_id) });
    }
  };
  const notifications = useSprintNotificationPoll(selectedSprint?.id, {
    onNew: (items) => showNotificationToasts(items, { onOpenTicket: openNotification }),
    refreshKey: refreshTrigger,
  });

  const handleSprintClick = (sprint) => {
    setSelectedSprint(sprint);
    setSelectedTicket(null);
    setTicketSearch("");
    setExpandedParents({});
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setDetailTab('Details');
    setParentTicketStack([]);
  };

  const handleChildTicketClick = (childTicket, returnTab) => {
    setParentTicketStack(prev => [...prev, { ticket: selectedTicket, tab: returnTab }]);
    setSelectedTicket(childTicket);
    setDetailTab('Details');
  };

  const handleBackToCards = () => {
    setSelectedSprint(null);
    setSelectedTicket(null);
    setTicketList([]);
    setParentTicketStack([]);
  };

  const handleBackToTickets = () => {
    if (parentTicketStack.length > 0) {
      const stack = [...parentTicketStack];
      const parent = stack.pop();
      setParentTicketStack(stack);
      setSelectedTicket(parent.ticket);
      setDetailTab(parent.tab);
    } else {
      setSelectedTicket(null);
    }
  };

  const toggleParentExpand = (parentId) => {
    setExpandedParents(prev => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  // Inline edit
  const handleInlineEdit = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
        method: "PUT", headers,
        body: JSON.stringify({ id: editRowId, sprint_id: selectedSprint.id, ...editedValues, company_id: 1 }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Ticket updated"); setEditRowId(null); setRefreshTrigger(p => !p); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Update failed"); }
  };

  // Side panel edit
  const handleSidePanelEdit = (ticket) => { setEditData(ticket); setIsSidebarOpen(true); };
  const handleSidePanelClose = () => { setIsSidebarOpen(false); setEditData(null); setRefreshTrigger(p => !p); };

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState(null); // ticket id to confirm

  const handleDeleteTicket = (ticketId) => {
    setDeleteConfirm(ticketId);
  };

  const confirmDeleteTicket = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/?id=${deleteConfirm}`, { method: "DELETE", headers });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast("Ticket deleted");
        setRefreshTrigger(p => !p);
        if (selectedTicket?.id === deleteConfirm) setSelectedTicket(null);
        // If the last bug of an open child story was deleted while on the Bugs tab,
        // fall back to Details (its Bugs tab disappears when the list is empty;
        // a parent story keeps the tab for "Add Bug").
        const bugs = selectedTicket?.bug_tickets || [];
        if (detailTab === 'Bugs' && selectedTicket?.story_type === 'Child' && bugs.length <= 1 && bugs.some(b => b.id === deleteConfirm)) setDetailTab('Details');
      }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Delete failed"); }
    finally { setDeleteConfirm(null); }
  };

  // Sprint CRUD
  const handleSaveSprint = async () => {
    try {
      const payload = { ...sprintForm, project_id: currentProject, company_id: 1, status: sprintForm.sprint_status || "ONGOING" };
      if (editSprintId) payload.id = editSprintId;
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/`, { method: editSprintId ? "PUT" : "POST", headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast(editSprintId ? "Sprint updated" : "Sprint created"); setShowSprintPopup(false); setEditSprintId(null); setSprintForm({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], sprint_status: "ONGOING", is_live_support: false }); setRefreshTrigger(p => !p); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Save failed"); }
  };

  const handleEditSprint = (sprint) => {
    const validStatuses = ["ONGOING", "COMPLETED", "OVERDUE", "CANCELLED"];
    const safeStatus = validStatuses.includes(sprint.sprint_status) ? sprint.sprint_status : "ONGOING";
    setSprintForm({ sprint_name: sprint.sprint_name, short_name: sprint.short_name || "", start_date: sprint.start_date || "", end_date: sprint.end_date || "", team_name: sprint.team_name || "", team_members: sprint.team_members || [], sprint_status: safeStatus, is_live_support: !!sprint.is_live_support });
    setEditSprintId(sprint.id);
    setShowSprintPopup(true);
  };

  // Release sheet (.xlsx) for the open sprint — stories only, bugs left out.
  const handleExportSprint = async (sprint) => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await sprintExportApi.exportSprint(sprint.id);
      const name = [sprint.short_name, sprint.sprint_name].filter(Boolean).join("_").replace(/[^A-Za-z0-9_-]+/g, "-");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name || "sprint"}_${new Date().toLocaleDateString("en-CA")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSuccessToast("Sprint exported");
    } catch (err) {
      showErrorToast(err.message || "Failed to export the sprint");
    } finally {
      setExporting(false);
    }
  };

  // Move to sprint
  const handleMoveTicket = async () => {
    if (!menuTicket || !moveTarget || moveSaving) return;
    if (isInProduction(menuTicket)) { showErrorToast(MOVE_BLOCKED_HINT); return; }
    setMoveSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/move-ticket/`, { method: "POST", headers, body: JSON.stringify({ ticket_id: menuTicket.id, new_sprint_id: moveTarget }) });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message || "Ticket moved"); setMovePopup(false); setMenuTicket(null); setRefreshTrigger(p => !p);
        invalidateRequirementsCache(); // its Client Requirement's sprint follows the ticket
        // Same as delete: moving an open child story's last bug away hides the Bugs tab.
        const bugs = selectedTicket?.bug_tickets || [];
        if (detailTab === 'Bugs' && selectedTicket?.story_type === 'Child' && bugs.length <= 1 && bugs.some(b => b.id === menuTicket.id)) setDetailTab('Details');
      }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Move failed"); }
    finally { setMoveSaving(false); }
  };

  // Released (Production) tickets stay in their sprint; the API refuses the move too.
  const MOVE_BLOCKED_HINT = "In Production — can't move to another sprint";
  const isInProduction = (t) => !!t && (isProductionStatus(t.status_id || t.status) || /production/i.test(t.status_name || t.ticket_status || ''));

  // Bugs move on their own (the parent story stays in this sprint).
  const openMoveBug = (bug) => {
    if (isInProduction(bug)) { showErrorToast(MOVE_BLOCKED_HINT); return; }
    setMenuTicket(bug);
    setMoveTarget(null);
    setMovePopup(true);
  };

  // Raise a move-to-live request for a ticket. Backend enforces the rules
  // (live-support sprint, QA/UAT-pass status, permission, no duplicate request).
  const handleSendToLive = async (ticket) => {
    if (!ticket) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/live-move/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ticket_id: ticket.id }),
      });
      const data = await res.json();
      if (data.status === "success") showSuccessToast("Live move request sent");
      else showErrorToast(data.message || "Could not send request");
    } catch (e) {
      showErrorToast("Could not send request");
    } finally {
      closeContextMenu();
    }
  };

  // Add child story or bug
  const openAddTicketPopup = (parentTicket, type) => {
    setAddTicketParent(parentTicket);
    setAddTicketType(type);
    setAddTicketForm({ short_description: "", status_id: ticketStatuses?.[0]?.id || "", priority_id: "", bug_type: "Development", due_date: "" });
    setAddTicketPopup(true);
    closeContextMenu();
  };

  const handleSaveNewTicket = async () => {
    if (!selectedSprint || !addTicketForm.short_description.trim()) {
      showErrorToast("Description is required");
      return;
    }
    try {
      const payload = {
        short_description: addTicketForm.short_description,
        status_id: addTicketForm.status_id || (ticketStatuses?.[0]?.id),
        sprint_id: selectedSprint.id,
        parent_ticket_id: addTicketParent ? addTicketParent.id : null,
        story_type: addTicketParent ? "Child" : "Parent",
        ticket_type: addTicketType,
        priority_id: addTicketForm.priority_id || null,
        due_date: addTicketForm.due_date || null,
        company_id: 1,
      };
      if (addTicketType === "Bug") {
        payload.bug_type = addTicketForm.bug_type || "Development";
      }
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, { method: "POST", headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(`${addTicketType} created`);
        setAddTicketPopup(false);
        setRefreshTrigger(p => !p);
        // Added from inside the open ticket → show it on the Bugs / Stories tab.
        if (addTicketParent && addTicketParent.id === selectedTicket?.id) setDetailTab(addTicketType === "Bug" ? "Bugs" : "Stories");
      } else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to create ticket"); }
  };

  // Comment
  // Returns whether it worked, so the comment box knows to clear itself.
  const handleAddComment = async (text, parentCommentId = null, files = []) => {
    if (!selectedTicket || (!text.trim() && !files.length)) return false;
    try {
      const data = await sprintApi.addComment(selectedTicket.id, text, files, parentCommentId);
      if (data.status === "success") { fetchComments(selectedTicket.id); return true; }
      showErrorToast(data.message || "Failed to add comment");
    } catch (e) { showErrorToast("Failed to add comment"); }
    return false;
  };

  const handleEditComment = async (commentId, text, files = [], removedIds = []) => {
    try {
      const data = await sprintApi.editComment(commentId, text, files, removedIds);
      if (data.status === "success") {
        if (data.message !== "Nothing to update") showSuccessToast("Comment updated");
        fetchComments(selectedTicket.id);
        return true;
      }
      showErrorToast(data.message || "Failed to update comment");
    } catch (e) { showErrorToast("Failed to update comment"); }
    return false;
  };

  // Comment files open in the shared viewer, with every comment file in its strip.
  const openCommentFiles = (files, index) => {
    setFileViewer({
      index,
      files: files.map(f => ({
        key: `cmt-${f.id}`, name: f.filename, by: f.uploaded_by, time: f.created_time,
        source: f.commented_by_name ? `Comment by ${f.commented_by_name}` : 'Comment',
        thumbUrl: f.url, getUrl: () => sprintApi.commentFileUrl(f.comment_id, f.id),
      })),
    });
  };

  // A fresh presigned URL for one attachment (list URLs expire after 10 min).
  const getAttachmentUrl = async (att) => {
    const res = await fetch(`${API_BASE_URL}ticketing/attachements/?id=${att.id}`, { headers });
    const data = await res.json();
    if (data.status !== "success" || !data.data) throw new Error(data.message || "Could not get the file");
    return data.data;
  };

  const handleDownload = async (att) => {
    try { await downloadFile(await getAttachmentUrl(att), att.filename); }
    catch (e) { showErrorToast(e?.message || "Download failed"); }
  };

  // Opens the full-screen viewer on `list[index]`, with every file in `list` in its strip.
  const openAttachmentViewer = (list, index) => {
    setFileViewer({
      index,
      files: list.map(att => ({
        key: `att-${att.id}`, name: att.filename, by: att.created_by, time: att.created_time,
        source: "Attachments", thumbUrl: att.url, getUrl: () => getAttachmentUrl(att),
      })),
    });
  };

  // Validates against the backend's rules, then uploads straight away — picked,
  // dropped or pasted. Bad files are listed inline (a toast is gone too fast).
  const uploadFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length || !selectedTicket) return;
    const rejected = [];
    const valid = picked
      // Pasted screenshots all arrive as "image.png"; give each its own name.
      .map((f, i) => (/^image\.(png|jpe?g)$/i.test(f.name)
        ? new File([f], `pasted-${new Date().toISOString().replace(/\D/g, '').slice(0, 14)}-${i + 1}.${fileExt(f.name)}`, { type: f.type })
        : f))
      .filter(f => {
        const ext = fileExt(f.name);
        if (!ATTACHMENT_EXTENSIONS.includes(ext)) { rejected.push({ name: f.name, reason: `${ext ? `.${ext}` : "Files without an extension"} not allowed` }); return false; }
        if (f.size > ATTACHMENT_MAX_MB * 1024 * 1024) { rejected.push({ name: f.name, reason: `Larger than ${ATTACHMENT_MAX_MB}MB` }); return false; }
        return true;
      });
    setSkippedFiles(rejected);
    if (!valid.length) return;
    const ticketId = selectedTicket.id;
    const batchId = Date.now() + Math.random();
    setUploadingBatches(b => [...b, { id: batchId, names: valid.map(f => f.name) }]);
    try {
      const formData = new FormData();
      formData.append("ticket_id", ticketId);
      valid.forEach((file) => formData.append("attachments", file));
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(valid.length === 1 ? "File uploaded" : `${valid.length} files uploaded`);
        fetchAttachments(ticketId);
      } else showErrorToast(data.message || "Upload failed");
    } catch (e) { showErrorToast("Upload failed"); }
    finally { setUploadingBatches(b => b.filter(x => x.id !== batchId)); }
  };
  uploadFilesRef.current = uploadFiles;

  // Delete attachment (the API allows the uploader or "Delete Ticket" holders)
  const handleDeleteAttachment = async (att) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/?id=${att.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        fetchAttachments(selectedTicket.id);
      } else showErrorToast(data.message || "Failed to delete attachment");
    } catch (e) { showErrorToast("Failed to delete attachment"); }
  };

  // Add link to ticket
  const handleAddLink = async () => {
    if (!linkUrl || !linkName || !selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/links/`, {
        method: "POST", headers,
        body: JSON.stringify({ ticket_id: selectedTicket.id, url: linkUrl, url_name: linkName }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        setLinkUrl(""); setLinkName(""); setShowAddLink(false);
        // Refresh ticket to get updated links
        const tRes = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${selectedTicket.sprint_id}&id=${selectedTicket.id}`, { headers });
        const tData = await tRes.json();
        if (tData.status === "success") setSelectedTicket(tData.data);
      } else showErrorToast(data.message || "Failed to add link");
    } catch (e) { showErrorToast("Failed to add link"); }
  };

  // Delete link
  const handleDeleteLink = async (linkItem) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/links/?ticket_link_id=${linkItem.id}`, {
        method: "DELETE", headers,
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        const tRes = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${selectedTicket.sprint_id}&id=${selectedTicket.id}`, { headers });
        const tData = await tRes.json();
        if (tData.status === "success") setSelectedTicket(tData.data);
      } else showErrorToast("Failed to delete link");
    } catch (e) { showErrorToast("Failed to delete link"); }
  };

  // Add branch to ticket
  const handleAddBranch = async () => {
    if (!selectedTicket || !addBranchForm.repository_id || !addBranchForm.branch_name.trim()) {
      showErrorToast("Repository and branch name are required");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/`, {
        method: "POST", headers,
        body: JSON.stringify({ ticket_id: selectedTicket.id, ...addBranchForm }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch added"); setShowAddBranch(false); setAddBranchForm({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' }); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to add branch"); }
  };

  // Sync branch from GitLab
  const handleSyncBranch = async (branchId) => {
    setBranchSyncing(branchId);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/sync/`, {
        method: "POST", headers,
        body: JSON.stringify({ id: branchId }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch synced"); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Sync failed"); }
    finally { setBranchSyncing(null); }
  };

  // Delete branch mapping
  const handleDeleteBranch = async (branchId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/?id=${branchId}`, { method: "DELETE", headers });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch removed"); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to remove branch"); }
  };

  // Context menu
  const openContextMenu = (e, ticket) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuTicket(ticket);

    // Diagnose why the "Send to Live" (Request Live Move) action shows or not.
    const hasFeature = Boolean(liveSupportEnabled);
    const sprintIsLive = Boolean(selectedSprint?.is_live_support);
    const hasPermission = Boolean(userPermission?.includes('Request Live Move'));
    if (hasFeature && sprintIsLive && hasPermission) {
      console.log("[Request Live Move] visible — all conditions met");
    } else {
      const reasons = [];
      if (!hasFeature) reasons.push("company feature 'liveSupportEnabled' is OFF (localStorage.liveSupportEnabled)");
      if (!sprintIsLive) reasons.push("selected sprint is NOT a live-support sprint (selectedSprint.is_live_support is falsy)");
      if (!hasPermission) reasons.push("user is missing the 'Request Live Move' permission (userPermissions)");
      console.log("[Request Live Move] hidden because:", reasons, {
        liveSupportEnabled: hasFeature,
        "selectedSprint.is_live_support": sprintIsLive,
        userPermission,
        hasRequestLiveMovePermission: hasPermission,
      });
    }
  };
  const closeContextMenu = () => { setMenuAnchorEl(null); };

  // ══════════ COMPUTED ══════════

  const filteredTickets = useMemo(() => {
    let result = ticketList;
    if (ticketSearch.trim()) {
      const q = ticketSearch.toLowerCase();
      result = result.filter(t =>
        (t.parent_code || '').toLowerCase().includes(q) ||
        (t.bug_code || '').toLowerCase().includes(q) ||
        (t.short_description || '').toLowerCase().includes(q) ||
        (t.assignee_name || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus) {
      result = result.filter(t => (t.ticket_status || '').toLowerCase() === filterStatus.toLowerCase());
    }
    if (filterPriority) {
      result = result.filter(t => (t.priority_name || '').toLowerCase() === filterPriority.toLowerCase());
    }
    if (filterAssigned) {
      // Developer or assignee on the story itself or on any of its child stories /
      // bugs, same as the QA filter below.
      const hasDev = (t) => {
        const devs = Array.isArray(t.developers) ? t.developers : [];
        return devs.some(d => String(d.id || d) === String(filterAssigned)) || String(t.assignee_id) === String(filterAssigned)
          || (t.child_tickets || []).some(hasDev) || (t.bug_tickets || []).some(hasDev);
      };
      result = result.filter(hasDev);
    }
    if (filterQA) {
      // QA or Peer QA on the story itself or on any of its child stories / bugs,
      // so a tester sees every row that holds some of their work.
      const hasQA = (t) => String(t.qa_id) === String(filterQA) || String(t.peer_qa_id) === String(filterQA)
        || (t.child_tickets || []).some(hasQA) || (t.bug_tickets || []).some(hasQA);
      result = result.filter(hasQA);
    }
    if (filterType) {
      result = result.filter(t => (t.ticket_type || '').toLowerCase() === filterType.toLowerCase());
    }
    if (dueSort) {
      // Tickets with no due date always sink to the bottom, in both directions —
      // an absent date is not "earliest", it is simply unset.
      result = [...result].sort((a, b) => {
        const av = a.due_date ? new Date(a.due_date).getTime() : null;
        const bv = b.due_date ? new Date(b.due_date).getTime() : null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return dueSort === 'asc' ? av - bv : bv - av;
      });
    }
    return result;
  }, [ticketList, ticketSearch, filterStatus, filterPriority, filterAssigned, filterQA, filterType, dueSort]);

  // All flat ticket codes for switcher (parents + children + bugs)
  const allTicketCodes = useMemo(() => {
    const codes = [];
    ticketList.forEach(t => {
      codes.push({ id: t.id, code: t.parent_code, type: t.ticket_type, ticket: t });
      if (t.child_tickets) {
        t.child_tickets.forEach(child => {
          codes.push({ id: child.id, code: child.child_code, type: child.ticket_type, ticket: child });
          if (child.bug_tickets) {
            child.bug_tickets.forEach(bug => {
              codes.push({ id: bug.id, code: bug.bug_code, type: 'Bug', ticket: bug });
            });
          }
        });
      }
      if (t.bug_tickets) {
        t.bug_tickets.forEach(bug => {
          codes.push({ id: bug.id, code: bug.bug_code, type: 'Bug', ticket: bug });
        });
      }
    });
    return codes;
  }, [ticketList]);

  // ══════════ RENDER HELPERS ══════════

  const categorizeStatus = (statusBreakdown) => {
    const completed = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('done') || k.toLowerCase().includes('completed') || k.toLowerCase().includes('closed')).reduce((s, [, v]) => s + v, 0);
    const inProgress = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('progress') || k.toLowerCase().includes('review') || k.toLowerCase().includes('qa') || k.toLowerCase().includes('dev')).reduce((s, [, v]) => s + v, 0);
    const todo = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('to do') || k.toLowerCase().includes('todo') || k.toLowerCase().includes('open') || k.toLowerCase().includes('new')).reduce((s, [, v]) => s + v, 0);
    const other = Object.values(statusBreakdown).reduce((s, v) => s + v, 0) - completed - inProgress - todo;
    return { completed, inProgress, todo, other };
  };

  // ══════════ RENDER ══════════

  // Reloads whatever screen is open: the sprint list (KPIs + dev analysis), the
  // sprint's tickets, or the open ticket (re-synced from the ticket list, which in
  // turn reloads its comments, attachments and branches).
  const renderRefresh = (loading, className = 'sl2-edit-sprint-btn') => (
    <AppTooltip title={loading ? 'Refreshing…' : 'Refresh'}>
      <span style={{ display: 'inline-flex' }}>
        <button className={`${className} sl2-refresh-btn`} onClick={() => setRefreshTrigger(p => !p)} disabled={loading} aria-label="Refresh">
          <FiRefreshCw size={13} className={loading ? 'sl2-refresh-btn__spin' : ''} />
        </button>
      </span>
    </AppTooltip>
  );

  return (
    <div className="sprint-dash">
      {/* ── Project selector + Add Sprint (hidden on Level 2/3) ── */}
      {!selectedSprint && (
        <div className="fd-selector-row" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FilterDropdown label="Project" value={currentProject} options={projects} onChange={v => { setCurrentProject(v); setSelectedSprint(null); setSelectedTicket(null); }} renderLabel={o => o?.project_name || ''} getValue={o => String(o?.id)} />
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {renderRefresh(kpiLoading)}
            <button className="fd-btn-primary" onClick={() => { setEditSprintId(null); setSprintForm({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], sprint_status: "ONGOING", is_live_support: false }); setShowSprintPopup(true); }}><FiPlus size={13} /> Add Sprint</button>
          </div>
        </div>
      )}

      {/* ── Sprint status tabs ── */}
      {!selectedSprint && (
        <div className="sprint-status-tabs" style={{ marginBottom: 6 }}>
          {SPRINT_STATUS_OPTIONS.map(opt => (
            <button key={opt.value} className={`sprint-status-tab ${sprintStatusFilter === opt.value ? 'sprint-status-tab--active' : ''}`} onClick={() => setSprintStatusFilter(opt.value)}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ LEVEL 1: KPI Cards ═══ */}
      {!selectedSprint && (
        kpiLoading ? (
          <div className="sprint-empty-state"><CircularProgress size={36} sx={{ color: 'var(--accent)' }} /></div>
        ) : kpiData.length === 0 ? (
          <div className="sprint-empty-state">No sprints found for this project and status filter.</div>
        ) : (
          <div className="sprint-kpi-grid">
            {kpiData.map(sprint => {
              const cat = categorizeStatus(sprint.status_breakdown);
              const total = sprint.total_tickets;
              const segments = [
                { count: cat.completed, color: "green" },
                { count: cat.inProgress, color: "blue" },
                { count: cat.todo, color: "yellow" },
                { count: cat.other, color: "gray" },
              ];
              return (
                <div key={sprint.id} className={`sprint-kpi-card`} onClick={() => handleSprintClick(sprint)}>
                  <div className="sprint-kpi-left">
                    <DonutChart segments={segments} total={total} />
                  </div>
                  <div className="sprint-kpi-right">
                    <div className="sprint-kpi-header">
                      <div>
                        <h3 className="sprint-kpi-name">{sprint.sprint_name} <span className="sprint-kpi-code">({sprint.short_name})</span></h3>
                      </div>
                      <span className={`sprint-status-pill ${STATUS_PILL_CLASS[sprint.sprint_status] || ''}`}>{sprint.sprint_status}</span>
                    </div>
                    <div className="sprint-kpi-meta">
                      <span>{formatDate(sprint.start_date)} – {formatDate(sprint.end_date)}</span>
                      {sprint.team_name && <span>Team: {sprint.team_name}</span>}
                    </div>
                    <div className="sprint-kpi-bars">
                      <KPIBar label="Done" count={cat.completed} total={total} color="green" />
                      <KPIBar label="In Progress" count={cat.inProgress} total={total} color="blue" />
                      <KPIBar label="To Do" count={cat.todo} total={total} color="yellow" />
                    </div>
                    {sprint.bug_count > 0 && (
                      <div className="sprint-kpi-bugs"><GoBug size={12} /> {sprint.bug_count} bug{sprint.bug_count !== 1 ? 's' : ''}</div>
                    )}
                  </div>
                  {/* Edit sprint button */}
                  <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, color: 'var(--text-subtle)' }} onClick={(e) => { e.stopPropagation(); handleEditSprint(sprint); }}>
                    <FiEdit2 size={13} />
                  </IconButton>
                  {/* Developer performance strip */}
                  {sprintDevData[sprint.id] && sprintDevData[sprint.id].length > 0 && (
                    <div className="sprint-kpi-devs" onClick={e => e.stopPropagation()}>
                      {sprintDevData[sprint.id].slice(0, 5).map((dev, di) => {
                        const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
                        const bg = avatarColors[(dev.name || '?').charCodeAt(0) % avatarColors.length];
                        const scoreColor = dev.score >= 70 ? '#4caf50' : dev.score >= 45 ? '#ff9800' : '#f44336';
                        return (
                          <div key={dev.name} className="sprint-kpi-dev">
                            <span className="sprint-kpi-dev__avatar" style={{ background: bg }}>{(dev.name || '?')[0].toUpperCase()}</span>
                            <div className="sprint-kpi-dev__info">
                              <span className="sprint-kpi-dev__name">{dev.name}</span>
                              <span className="sprint-kpi-dev__stat">{dev.done}/{dev.tickets} done{dev.inProg > 0 ? ` · ${dev.inProg} active` : ''}</span>
                            </div>
                            <div className="sprint-kpi-dev__bar"><div style={{ width: `${dev.completionRate}%`, background: scoreColor }} /></div>
                            <div className="sprint-kpi-dev__score" style={{ color: scoreColor, borderColor: scoreColor }}>{dev.score}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ═══ LEVEL 2: Sprint Ticket Dashboard ═══ */}
      {selectedSprint && !selectedTicket && (() => {
        // Status-wise breakdown across Story + Epic only (parents + non-Bug children).
        // Bugs are counted separately via the Bugs KPI, so excluding them here keeps the
        // Done / In Progress / To Do counts consistent with Total Tickets.
        const statusMap = {};
        const walk = (list) => {
          list.forEach(t => {
            if (t.ticket_type === 'Bug') return;
            const s = t.status_name || t.ticket_status || 'Unknown';
            statusMap[s] = (statusMap[s] || 0) + 1;
            if (t.child_tickets) walk(t.child_tickets);
          });
        };
        walk(ticketList);
        const todoStatuses = /^(to\s*do|hold)$/i;
        const doneStatuses = /^(production|live|moved\s*to\s*live|moved\s*to\s*production|not\s*a\s*bug)$/i;
        const todoCount = Object.entries(statusMap).filter(([k]) => todoStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const doneCount = Object.entries(statusMap).filter(([k]) => doneStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const inProgCount = Object.entries(statusMap).filter(([k]) => !todoStatuses.test(k.trim()) && !doneStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const completionPct = ticketCount > 0 ? Math.round((doneCount / ticketCount) * 100) : 0;

        // Use master values for filter dropdowns (not just what's in current tickets)
        const masterStatuses = ticketStatuses.filter(s => s.is_active).map(s => s.ticket_status);
        const masterPriorities = priorityTypes.filter(p => p.is_active).map(p => p.priority);
        const masterEmployees = employees.filter(e => e.is_active).map(e => ({ id: e.id, name: e.name }));
        const hasActiveFilters = filterStatus || filterPriority || filterAssigned || filterQA;

        // Custom filter dropdown component
        const FilterDrop = ({ label, value, options, onChange, renderLabel, minWidth }) => {
          const [open, setOpen] = React.useState(false);
          const [search, setSearch] = React.useState('');
          const ref = React.useRef(null);

          React.useEffect(() => {
            const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
          }, []);

          const filtered = search ? options.filter(o => {
            const lbl = renderLabel ? renderLabel(o) : String(o);
            return lbl.toLowerCase().includes(search.toLowerCase());
          }) : options;

          const selectedLabel = value ? (renderLabel ? renderLabel(options.find(o => (typeof o === 'object' ? String(o.id) : o) === String(value))) : value) : null;

          return (
            <div className="sl2-fdrop" ref={ref}>
              <button className={`sl2-fdrop__trigger ${value ? 'sl2-fdrop__trigger--active' : ''}`} onClick={() => setOpen(!open)}
                style={minWidth ? { minWidth, justifyContent: 'space-between' } : undefined}>
                <span>{selectedLabel || label}</span>
                <FiChevronDown size={12} className={`sl2-fdrop__chevron ${open ? 'sl2-fdrop__chevron--open' : ''}`} />
              </button>
              {open && (
                <div className="sl2-fdrop__panel">
                  <div className="sl2-fdrop__search">
                    <FiSearch size={12} />
                    <input type="text" placeholder={`Search ${label.toLowerCase()}...`} value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                  </div>
                  <div className="sl2-fdrop__list">
                    <div className={`sl2-fdrop__item ${!value ? 'sl2-fdrop__item--selected' : ''}`} onClick={() => { onChange(''); setOpen(false); setSearch(''); }}>
                      All {label}
                    </div>
                    {filtered.map(o => {
                      const val = typeof o === 'object' ? String(o.id) : o;
                      const lbl = renderLabel ? renderLabel(o) : String(o);
                      const isSelected = String(value) === val;
                      return (
                        <div key={val} className={`sl2-fdrop__item ${isSelected ? 'sl2-fdrop__item--selected' : ''}`} onClick={() => { onChange(val); setOpen(false); setSearch(''); }}>
                          {lbl}
                          {isSelected && <span className="sl2-fdrop__check">✓</span>}
                        </div>
                      );
                    })}
                    {filtered.length === 0 && <div className="sl2-fdrop__empty">No results</div>}
                  </div>
                </div>
              )}
            </div>
          );
        };

        // Developer avatar helper
        const DevAvatar = ({ name }) => {
          const colors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b'];
          const initial = (name || '?')[0].toUpperCase();
          const color = colors[initial.charCodeAt(0) % colors.length];
          return <AppTooltip title={name}><span style={{ width: 28, height: 28, borderRadius: '50%', background: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, marginLeft: -6, border: '2px solid var(--bg-secondary)' }}>{initial}</span></AppTooltip>;
        };

        // Priority pill
        const PrioPill = ({ name }) => {
          const n = (name || '').toLowerCase();
          const color = n.includes('high') ? 'var(--status-red)' : n.includes('low') ? 'var(--status-green)' : '#f5a623';
          return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', color }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />{name || '-'}</span>;
        };

        // Status pill with auto color
        const StatusPill = ({ name }) => {
          const n = (name || '').toLowerCase();
          let cls = 'fd-status-pill--muted';
          if (/done|completed|live|production/i.test(n)) cls = 'fd-status-pill--success';
          else if (/progress|dev|qa|review/i.test(n)) cls = 'fd-status-pill--info';
          else if (/fail|reject|block/i.test(n)) cls = 'fd-status-pill--danger';
          return <span className={`fd-status-pill ${cls}`}>{name || '-'}</span>;
        };

        return (
        <div className="sl2-page">
          {/* ── Header: Title + Create button ── */}
          <div className="sl2-header">
            <div className="sl2-header__left">
              <button className="sl2-back-btn" onClick={handleBackToCards}>←</button>
              <h1 className="sl2-title">{selectedSprint.sprint_name}</h1>
              <span className={`sprint-status-pill ${STATUS_PILL_CLASS[selectedSprint.sprint_status] || ''}`}>{selectedSprint.sprint_status}</span>
            </div>
            <div className="sl2-header__right">
              <SprintNotificationBell
                sprint={selectedSprint}
                count={notifications.count}
                onChanged={notifications.refresh}
                onOpenNotification={openNotification}
              />
              {renderRefresh(ticketLoading)}
              <AppTooltip title="Export release sheet (.xlsx)">
                <span>
                  <button className="sl2-edit-sprint-btn sl2-export-btn" onClick={() => handleExportSprint(selectedSprint)} disabled={exporting}>
                    {exporting ? <span className="sl2-export-btn__spin" /> : <FiDownload size={13} />} Export
                  </button>
                </span>
              </AppTooltip>
              <button className="sl2-edit-sprint-btn" onClick={() => handleEditSprint(selectedSprint)}><FiEdit2 size={13} /></button>
            </div>
          </div>

          {/* ── KPI Cards ── */}
          <div className="sl2-kpi-row">
            <div className="sl2-kpi">
              <div className="sl2-kpi__ring">
                <svg viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--border-subtle)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${completionPct * 0.974} ${97.4 - completionPct * 0.974}`} transform="rotate(-90 18 18)" />
                </svg>
                <span className="sl2-kpi__ring-val">{completionPct}%</span>
              </div>
              <div className="sl2-kpi__info">
                <span className="sl2-kpi__val">{ticketCount}</span>
                <span className="sl2-kpi__label">Total Tickets</span>
              </div>
            </div>
            <div className="sl2-kpi sl2-kpi--green">
              <span className="sl2-kpi__val">{doneCount}</span>
              <span className="sl2-kpi__label">Done</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (doneCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--blue">
              <span className="sl2-kpi__val">{inProgCount}</span>
              <span className="sl2-kpi__label">In Progress</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (inProgCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--orange">
              <span className="sl2-kpi__val">{todoCount}</span>
              <span className="sl2-kpi__label">To Do</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (todoCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--red">
              <span className="sl2-kpi__val">{bugCount}</span>
              <span className="sl2-kpi__label">Bugs</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${(ticketCount + bugCount) ? (bugCount / (ticketCount + bugCount)) * 100 : 0}%` }} /></span>
            </div>
          </div>

          {/* ── Toolbar: Count + Filters + Search ── */}
          <div className="sl2-toolbar">
            <span className="sl2-toolbar__count">{filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}</span>
            <div className="sl2-toolbar__filters">
              <FilterDrop label="Status" value={filterStatus} options={masterStatuses} onChange={setFilterStatus} />
              <FilterDrop label="Priority" value={filterPriority} options={masterPriorities} onChange={setFilterPriority} />
              <FilterDrop label="Assigned" value={filterAssigned} options={masterEmployees} onChange={(v) => setFilterAssigned(v)} renderLabel={(o) => o?.name || ''} />
              <FilterDrop label="QA" value={filterQA} options={masterEmployees} onChange={(v) => setFilterQA(v)} renderLabel={(o) => o?.name || ''} minWidth={120} />
              {hasActiveFilters && (
                <button className="sl2-filter-pill sl2-filter-pill--clear" onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterAssigned(''); setFilterQA(''); }}>
                  <FiX size={11} /> Clear
                </button>
              )}
            </div>
            <div className="sl2-toolbar__search">
              <input type="text" placeholder="Search..." value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} />
              <FiSearch size={14} />
            </div>
            {canImportCases && (
              <AppTooltip title="Upload a sheet of test cases and link them to this sprint's tickets">
                <button className="sl2-fdrop__trigger" onClick={() => setImportingCases(true)}>
                  <FiUploadCloud size={12} /> Import test cases
                </button>
              </AppTooltip>
            )}
            {liveSupportEnabled && selectedSprint?.is_live_support && (
              <button className="fd-btn-primary" onClick={() => openAddTicketPopup(null, 'Story')}>
                <FiPlus size={13} /> Add Ticket
              </button>
            )}
          </div>

          {importingCases && (
            <TestCaseImportModal
              sprintId={selectedSprint.id}
              sprintName={selectedSprint.sprint_name}
              sprintTickets={allTicketCodes.filter((t) => t.code).map((t) => ({ id: t.id, code: t.code }))}
              onClose={() => setImportingCases(false)}
              onImported={() => setImportingCases(false)}
            />
          )}

          {/* ── Table ── */}
          <div className="sl2-table-card">
            {ticketLoading ? (
              <div className="sprint-empty-state"><CircularProgress size={32} sx={{ color: 'var(--accent)' }} /></div>
            ) : (
              <table className="sl2-table">
                <thead>
                  <tr>
                    <th className="sl2-th--ticket">Ticket No</th>
                    <th className="sl2-th--desc">Description</th>
                    <th className="sl2-th--status">Status</th>
                    <th className="sl2-th--prio">Priority</th>
                    <AppTooltip title="Sort by due date">
                      <th
                        className="sl2-th--due sl2-th--sortable"
                        onClick={() => setDueSort(p => (p === 'asc' ? 'desc' : p === 'desc' ? '' : 'asc'))}
                      >
                        Due Date{dueSort === 'asc' ? ' ▲' : dueSort === 'desc' ? ' ▼' : ''}
                      </th>
                    </AppTooltip>
                    <th className="sl2-th--assigned">Assigned</th>
                    <th className="sl2-th--qa">QA Assigned</th>
                    <th className="sl2-th--overview">Overview</th>
                    <th className="sl2-th--action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length > 0 ? filteredTickets.map((ticket, idx) => {
                    const hasChildren = ticket.child_tickets?.length > 0;
                    const hasBugs = ticket.bug_tickets?.length > 0;
                    const isExp = expandedParents[ticket.id];

                    const renderRow = (t, indent, key) => {
                      const tCode = t.parent_code || t.child_code || t.bug_code;
                      const tDevArr = Array.isArray(t.developers) ? t.developers : [];
                      const tType = t.ticket_type || (t.bug_code ? 'Bug' : 'Story');
                      return (
                        <tr key={key || t.id} className="sl2-row" onClick={() => handleTicketClick(t)}>
                          <td style={{ paddingLeft: indent }}>
                            <span className="sl2-ticket-code">{tCode}</span>
                            {tType !== 'Bug' && t.bug_code && (
                              <AppTooltip title={`Moved from another sprint; this was bug ${t.bug_code}`}>
                                <span className="sl2-bug-chip" style={{ marginLeft: 6 }}>
                                  <FiGitMerge size={10} /> from {t.bug_code}
                                </span>
                              </AppTooltip>
                            )}
                          </td>
                          <td>
                            <TitleTooltip title={t.short_description}><span className="sl2-ticket-desc">{t.short_description || '-'}</span></TitleTooltip>
                          </td>
                          <td className="sl2-td--status" onClick={e => openStatusDrop(e, t.id)}>
                            <StatusPill name={t.status_name || t.ticket_status} />
                          </td>
                          <td><PrioPill name={t.priority_name} /></td>
                          <td><DueDateCell value={t.due_date} /></td>
                          <td>
                            <div className="sl2-assigned-cell">
                              {tDevArr.slice(0, 3).map((d, i) => <DevAvatar key={d.id || i} name={d.name || d} />)}
                              {tDevArr.length > 3 && <span className="sl2-dev-overflow">+{tDevArr.length - 3}</span>}
                              {tDevArr.length > 0 && <span className="sl2-dev-name">{tDevArr[0]?.name || tDevArr[0]}</span>}
                              {tDevArr.length === 0 && <span className="sl2-no-assign">-</span>}
                            </div>
                          </td>
                          <td>
                            <div className="sl2-assigned-cell">
                              {t.qa_name ? (
                                <>
                                  <DevAvatar name={t.qa_name} />
                                  <span className="sl2-dev-name">{t.qa_name}</span>
                                </>
                              ) : <span className="sl2-no-assign">-</span>}
                            </div>
                          </td>
                          <td className="sl2-overview-cell">
                            {(t.bug_count > 0 || t.child_count > 0 || t.child_tickets?.length > 0) ? (
                              <div className="sl2-overview-chips">
                                {(t.child_count > 0 || t.child_tickets?.length > 0) && <span className="sl2-child-chip"><FiGitBranch size={10} /> {t.child_count || t.child_tickets?.length}</span>}
                                {t.bug_count > 0 && <span className="sl2-bug-chip"><GoBug size={10} /> {t.bug_count}</span>}
                              </div>
                            ) : '-'}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="sl2-action-cell">
                              {(tType === 'Epic' || tType === 'Story') ? (
                                <AppTooltip title="Actions">
                                  <IconButton size="small" onClick={(e) => openContextMenu(e, t)}>
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </AppTooltip>
                              ) : (
                                <>
                                  {tType === 'Bug' && (
                                    <AppTooltip title={isInProduction(t) ? MOVE_BLOCKED_HINT : "Move to Sprint"}>
                                      <span style={{ display: 'inline-flex' }}>
                                        <button className="fd-action-btn" onClick={() => openMoveBug(t)} disabled={isInProduction(t)}><FiGitMerge size={12} /></button>
                                      </span>
                                    </AppTooltip>
                                  )}
                                  {userPermission?.includes('Delete Ticket') && <AppTooltip title="Delete"><button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDeleteTicket(t.id)}><FiTrash2 size={12} /></button></AppTooltip>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    };

                    return (
                      <React.Fragment key={ticket.id}>
                        {renderRow(ticket, 14, ticket.id)}
                        {isExp && ticket.child_tickets?.map(child => (
                          <React.Fragment key={child.id}>
                            {renderRow(child, 34, child.id)}
                            {child.bug_tickets?.map(bug => renderRow(bug, 54, bug.id))}
                          </React.Fragment>
                        ))}
                        {isExp && ticket.bug_tickets?.map(bug => renderRow(bug, 34, bug.id))}
                      </React.Fragment>
                    );
                  }) : (
                    <tr><td colSpan={9} className="sl2-empty">{ticketSearch || hasActiveFilters ? 'No tickets match your filters' : 'No tickets in this sprint'}</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
        );
      })()}

      {/* ═══ Inline Status Dropdown (fixed position) ═══ */}
      {editingStatusId && (() => {
        // Find the ticket being edited across all levels
        let editTicket = null;
        for (const t of ticketList) {
          if (t.id === editingStatusId) { editTicket = t; break; }
          if (t.child_tickets) for (const c of t.child_tickets) {
            if (c.id === editingStatusId) { editTicket = c; break; }
            if (c.bug_tickets) for (const b of c.bug_tickets) { if (b.id === editingStatusId) { editTicket = b; break; } }
          }
          if (t.bug_tickets) for (const b of t.bug_tickets) { if (b.id === editingStatusId) { editTicket = b; break; } }
          if (editTicket) break;
        }
        if (!editTicket && selectedTicket) {
          editTicket = [...(selectedTicket.bug_tickets || []), ...(selectedTicket.child_tickets || [])]
            .find(x => x.id === editingStatusId) || null;
        }
        if (!editTicket) return null;
        // Portalled to <body>: a positioned/animated ancestor turns position:fixed
        // into "relative to that ancestor" and caps its stacking order, which
        // left the menu behind the L3 workspace.
        return createPortal(
          <div className="sl2-status-edit" style={{ top: statusDropPos.top, left: statusDropPos.left }} onClick={e => e.stopPropagation()}>
            {ticketStatuses.filter(s => s.is_active).map(s => (
              <div key={s.id} className={`sl2-status-option ${String(editTicket.status_id || editTicket.status) === String(s.id) ? 'sl2-status-option--current' : ''}`} onClick={() => handleInlineStatusChange(editTicket, s.id)}>
                {s.ticket_status}
              </div>
            ))}
          </div>,
          document.body
        );
      })()}

      {/* ═══ File viewer (full screen, portalled above the L3 workspace) ═══ */}
      {fileViewer && (
        <FileViewer
          files={fileViewer.files}
          index={fileViewer.index}
          onIndexChange={i => setFileViewer(v => (v ? { ...v, index: i } : v))}
          onClose={() => setFileViewer(null)}
        />
      )}

      {/* ═══ Delete Confirmation ═══ */}
      {deleteConfirm && (
        <div className="sl2-qc-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="sl2-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-confirm-icon"><FiTrash2 size={22} /></div>
            <h3>Delete Ticket</h3>
            <p>Are you sure you want to delete this ticket? This action cannot be undone.</p>
            <div className="sl2-confirm-actions">
              <button className="sl2-qc-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="sl2-confirm-delete" onClick={confirmDeleteTicket}><FiTrash2 size={13} /> Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Move Back to Client Requirements ═══ */}
      {moveBackTicket && (
        <div className="sl2-qc-overlay" onClick={() => setMoveBackTicket(null)}>
          <div className="sl2-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-confirm-icon"><FiRotateCcw size={22} /></div>
            <h3>Move Back to Client Requirements</h3>
            {moveBackTicket.client_requirement_id ? (
              <p>
                This point is mapped to a Client Requirement. It will be removed from
                the sprint, its status reset to <strong>To Do</strong>, and the
                requirement returned to the queue. If moved to a sprint again, the
                same ticket is reused — no duplicates.
              </p>
            ) : (
              <>
                <p>
                  This point was created directly in the sprint. A new Client
                  Requirement will be created with its description, user story and
                  acceptance criteria, and the point will be removed from the sprint.
                  Its history will show it originated from this sprint.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', margin: '10px 0' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Client *</label>
                  <select className="crd-sprint-select" value={moveBackClientId} onChange={e => setMoveBackClientId(e.target.value)}>
                    <option value="" disabled>Select Client</option>
                    {crClients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Status (optional)</label>
                  <select className="crd-sprint-select" value={moveBackStatusId} onChange={e => setMoveBackStatusId(e.target.value)}>
                    <option value="">Default (initial status)</option>
                    {crStatuses.map(s => <option key={s.id} value={s.id}>{s.client_requirement_status}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="sl2-confirm-actions">
              <button className="sl2-qc-cancel" onClick={() => setMoveBackTicket(null)}>Cancel</button>
              <button className="sl2-qc-submit" onClick={handleMoveBack} disabled={moveBackSaving || (!moveBackTicket.client_requirement_id && !moveBackClientId)}>
                {moveBackSaving ? 'Moving…' : <><FiRotateCcw size={13} /> Move Back</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ LEVEL 3: Enhanced Ticket Workspace ═══ */}
      {selectedTicket && (() => {
        const tCode = selectedTicket.parent_code || selectedTicket.child_code || selectedTicket.bug_code;
        const hasArtifact = !!selectedTicket.artifact;
        const childTickets = selectedTicket.child_tickets || [];
        const bugTickets = selectedTicket.bug_tickets || [];
        const pickedBugs = bugTickets.filter(b => selectedBugIds.includes(b.id));
        const allBugsPicked = bugTickets.length > 0 && pickedBugs.length === bugTickets.length;
        const toggleBug = (id) => setSelectedBugIds(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
        const isEpic = (selectedTicket.ticket_type || '').toLowerCase() === 'epic';
        const isStory = (selectedTicket.ticket_type || '').toLowerCase() === 'story';
        // Only parent stories take new bugs from inside the ticket, so their Bugs tab shows even when empty.
        const isParentStory = isStory && (selectedTicket.story_type || 'Parent') === 'Parent';
        const L3_TABS = [
          'Details', 'Story & Criteria', 'Attachments', 'Branches',
          ...(qualityEnabled ? ['Test Cases'] : []),
          ...(isEpic && childTickets.length > 0 ? ['Stories'] : []),
          ...(isStory && (bugTickets.length > 0 || isParentStory) ? ['Bugs'] : []),
          ...(hasArtifact ? ['Normalized', 'Dev Spec', 'QA Plan'] : []),
        ];
        const [activeTab, setActiveTab] = [detailTab, setDetailTab];
        const tType = selectedTicket.ticket_type || 'Story';
        const tStatus = selectedTicket.status_name || selectedTicket.ticket_status || '-';

        return (
        <>
          {/* Top bar */}
          <div className="l3-topbar">
            <div className="l3-topbar__left">
              <button className="l3-back-btn" onClick={() => { handleBackToTickets(); setIsInlineEditing(false); setEditedTicket(null); }}>←</button>
              <span className="l3-topbar__sprint">{selectedSprint?.sprint_name}</span>
              <span className="l3-topbar__sep">/</span>
              <span className="l3-topbar__code">{tCode}</span>
              {tCode && <CopyButton text={tCode} title="Copy ticket no" />}
            </div>
            <div className="l3-topbar__right">
              {renderRefresh(ticketLoading || commentsLoading, 'l3-edit-btn')}
              <div className="l3-switcher-wrap">
                <AppTooltip title="Switch ticket">
                  <button className="l3-switcher-btn"><FiChevronDown size={14} /> <span>{allTicketCodes.length} tickets</span></button>
                </AppTooltip>
                <div className="l3-switcher-panel">
                  <div className="l3-switcher-panel__header">All Tickets</div>
                  <div className="l3-switcher-panel__list">
                    {allTicketCodes.map(tc => (
                      <div key={tc.id} className={`l3-switcher-item ${selectedTicket.id === tc.id ? 'l3-switcher-item--active' : ''}`} onClick={() => { if (tc.ticket) { handleTicketClick(tc.ticket); setIsInlineEditing(false); setEditedTicket(null); } }}>
                        <span className="l3-switcher-item__dot" style={{ background: tc.type === 'Bug' ? 'var(--status-red)' : tc.type === 'Epic' ? 'var(--accent)' : 'var(--status-blue)' }} />
                        <span className="l3-switcher-item__code">{tc.code}</span>
                        <TitleTooltip title={tc.ticket?.short_description}><span className="l3-switcher-item__desc">{tc.ticket?.short_description || ''}</span></TitleTooltip>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {isInlineEditing ? (
                <>
                  <button className="l3-save-btn" onClick={handleInlineSave} disabled={editSaving}>{editSaving ? 'Saving...' : <><FiSave size={13} /> Save</>}</button>
                  <button className="l3-cancel-btn" onClick={() => { setIsInlineEditing(false); setEditedTicket(null); }}><FiXCircle size={13} /> Cancel</button>
                </>
              ) : (
                <>
                {isEpic && (
                  <button className="l3-edit-btn" onClick={() => openAddTicketPopup(selectedTicket, 'Story')}>
                    <FiPlus size={13} /> Add Story
                  </button>
                )}
                {isParentStory && (
                  <button className="l3-edit-btn" onClick={() => openAddTicketPopup(selectedTicket, 'Bug')}>
                    <GoBug size={13} /> Add Bug
                  </button>
                )}
                {(isEpic || isStory) && (selectedTicket.story_type || 'Parent') === 'Parent' && (
                  <AppTooltip title="Move this point back to Client Requirements">
                    <button className="l3-cancel-btn" onClick={() => openMoveBack(selectedTicket)}>
                      <FiRotateCcw size={13} /> Move to Requirements
                    </button>
                  </AppTooltip>
                )}
                <button className="l3-edit-btn" onClick={() => {
                  const t = { ...selectedTicket };
                  // Convert JSON arrays/objects to readable text for editing
                  const toText = (val) => {
                    if (!val) return '';
                    let data = val;
                    // If it's a string that looks like JSON, try to parse it
                    if (typeof data === 'string') {
                      const s = data.trim();
                      if ((s.startsWith('[') || s.startsWith('{')) && (s.endsWith(']') || s.endsWith('}'))) {
                        try { data = JSON.parse(s.replace(/'/g, '"')); } catch { return data; }
                      } else { return data; }
                    }
                    // Now data is parsed — format it
                    if (Array.isArray(data)) {
                      return data.map(item => {
                        if (typeof item === 'string') return item;
                        if (typeof item === 'object' && item && (item.given || item.when || item.then))
                          return `Given ${item.given || ''}, When ${item.when || ''}, Then ${item.then || ''}`;
                        return typeof item === 'object' ? Object.values(item).join(' — ') : String(item);
                      }).join('\n');
                    }
                    if (typeof data === 'object' && data) {
                      if (data.given) return `Given ${data.given}, When ${data.when || ''}, Then ${data.then || ''}`;
                      return Object.values(data).join(' — ');
                    }
                    return String(data);
                  };
                  t.user_story = toText(t.user_story);
                  t.acceptance_criteria = toText(t.acceptance_criteria);
                  setIsInlineEditing(true);
                  setEditedTicket(t);
                }}><FiEdit2 size={13} /> Edit</button>
                </>
              )}
            </div>
          </div>

          {/* Main workspace */}
          <div className="l3-workspace">
            <div className="l3-detail">
              {/* Hero — View or Edit mode */}
              <div className={`l3-hero ${isInlineEditing ? 'l3-hero--editing' : ''}`}>
                {isInlineEditing ? (
                  <>
                    <input className="l3-inline-input l3-inline-input--title" value={editedTicket.short_description || ''} onChange={e => setEditedTicket(p => ({ ...p, short_description: e.target.value }))} placeholder="Ticket title" />
                    <div className="l3-edit-grid">
                      <div className="l3-edit-field">
                        <label>Type</label>
                        <select value={editedTicket.ticket_type || ''} onChange={e => setEditedTicket(p => ({ ...p, ticket_type: e.target.value }))}>
                          {ticketTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Status</label>
                        <select value={editedTicket.status_id || editedTicket.status || ''} onChange={e => setEditedTicket(p => ({ ...p, status_id: e.target.value }))}>
                          {ticketStatuses.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.ticket_status}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Priority</label>
                        <select value={editedTicket.priority_id || editedTicket.priority || ''} onChange={e => setEditedTicket(p => ({ ...p, priority_id: e.target.value }))}>
                          <option value="">-</option>
                          {priorityTypes.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.priority}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Story Points</label>
                        <select value={editedTicket.story_points || ''} onChange={e => setEditedTicket(p => ({ ...p, story_points: e.target.value }))}>
                          <option value="">-</option>
                          {[1,2,3,5,8,13].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Assignee</label>
                        <select value={editedTicket.assignee_id || editedTicket.assignee || ''} onChange={e => setEditedTicket(p => ({ ...p, assignee_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Developers</label>
                        <Autocomplete
                          multiple size="small"
                          options={employees.filter(e => e.is_active)}
                          getOptionLabel={o => o.name || ''}
                          value={editedTicket.developers || []}
                          onChange={(_, v) => setEditedTicket(p => ({ ...p, developers: v }))}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          renderInput={params => <TextField {...params} placeholder="Select..." variant="outlined" sx={{ '& .MuiOutlinedInput-root': { padding: '2px 6px', fontSize: '0.74rem' } }} />}
                        />
                      </div>
                      <div className="l3-edit-field">
                        <label>QA</label>
                        <select value={editedTicket.qa_id || editedTicket.qa || ''} onChange={e => setEditedTicket(p => ({ ...p, qa_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Peer QA</label>
                        <select value={editedTicket.peer_qa_id || editedTicket.peer_qa || ''} onChange={e => setEditedTicket(p => ({ ...p, peer_qa_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>UAT</label>
                        <select value={editedTicket.uat_id || editedTicket.uat || ''} onChange={e => setEditedTicket(p => ({ ...p, uat_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Dev Est. (hrs)</label>
                        <input type="number" min="1" max="60" value={editedTicket.dev_est_time || ''} onChange={e => setEditedTicket(p => ({ ...p, dev_est_time: e.target.value }))} />
                      </div>
                      <div className="l3-edit-field">
                        <label>QA Est. (hrs)</label>
                        <input type="number" min="1" max="60" value={editedTicket.qa_est_time || ''} onChange={e => setEditedTicket(p => ({ ...p, qa_est_time: e.target.value }))} />
                      </div>
                      <div className="l3-edit-field">
                        <label>Due Date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                        <input type="date" value={editedTicket.due_date || ''} onChange={e => setEditedTicket(p => ({ ...p, due_date: e.target.value }))} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="l3-hero__title-row">
                      <h1 className="l3-hero__title">{selectedTicket.short_description}</h1>
                      {selectedTicket.short_description && (
                        <CopyButton text={[tCode, selectedTicket.short_description].filter(Boolean).join(' - ')} title="Copy ticket no and title" />
                      )}
                    </div>
                    <div className="l3-hero__meta">
                      <span className={`sl2-type-badge sl2-type-badge--${tType.toLowerCase()}`}>{tType}</span>
                      {tType === 'Bug' ? (
                        <AppTooltip title="Change bug type">
                          <span className="l3-meta-chip l3-meta-chip--red" role="button" tabIndex={0} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }} onClick={e => openBugTypeMenu(e, selectedTicket)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openBugTypeMenu(e, selectedTicket); }}>
                            {selectedTicket.bug_type || 'Set bug type'} <FiChevronDown size={10} />
                          </span>
                        </AppTooltip>
                      ) : selectedTicket.bug_type && <span className="l3-meta-chip l3-meta-chip--red">{selectedTicket.bug_type}</span>}
                      <span className={`fd-status-pill ${/done|completed|live|production/i.test(tStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(tStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(tStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`}>{tStatus}</span>
                      <span className="l3-meta-chip">{selectedTicket.priority_name || '-'}</span>
                      {selectedTicket.story_points && <span className="l3-meta-chip">SP: {selectedTicket.story_points}</span>}
                    </div>
                    <div className="l3-hero__details-grid">
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">Assignee</span><span>{selectedTicket.assignee_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUsers size={11} /><span className="l3-detail-item__label">Developers</span><span>{selectedTicket.developers?.length > 0 ? selectedTicket.developers.map(d => d.name || d).join(', ') : '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">QA</span><span>{selectedTicket.qa_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">Peer QA</span><span>{selectedTicket.peer_qa_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">UAT</span><span>{selectedTicket.uat_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">Dev Est.</span><span>{selectedTicket.dev_est_time ? `${selectedTicket.dev_est_time}h` : '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">QA Est.</span><span>{selectedTicket.qa_est_time ? `${selectedTicket.qa_est_time}h` : '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">Due Date</span><DueDateCell value={selectedTicket.due_date} /></div>
                    </div>
                  </>
                )}
              </div>

              {/* Tabs */}
              <div className="l3-tabs">
                {L3_TABS.map(tab => (
                  <button key={tab} className={`l3-tab ${activeTab === tab ? 'l3-tab--active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab}
                    {tab === 'Attachments' && (ticketAttachments.length + (selectedTicket?.links?.length || 0)) > 0 && <span className="l3-tab__badge">{ticketAttachments.length + (selectedTicket?.links?.length || 0)}</span>}
                    {tab === 'Branches' && ticketBranches.length > 0 && <span className="l3-tab__badge">{ticketBranches.length}</span>}
                    {tab === 'Test Cases' && testCaseCount > 0 && <span className="l3-tab__badge">{testCaseCount}</span>}
                    {tab === 'Stories' && childTickets.length > 0 && <span className="l3-tab__badge">{childTickets.length}</span>}
                    {tab === 'Bugs' && bugTickets.length > 0 && <span className="l3-tab__badge" style={{ background: '#f44336' }}>{bugTickets.length}</span>}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="l3-body">
                {activeTab === 'Details' && (
                  <>
                    <div className="sprint-detail-section">
                      <div className="l3-section-head">
                        <h4>Description</h4>
                        {!isInlineEditing && selectedTicket.detailed_description && (
                          <CopyButton text={selectedTicket.detailed_description} label="Copy" title="Copy description" />
                        )}
                      </div>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.detailed_description || ''} onChange={e => setEditedTicket(p => ({ ...p, detailed_description: e.target.value }))} placeholder="Add a detailed description..." rows={6} />
                      ) : (
                        <p>{selectedTicket.detailed_description || <span style={{ color: 'var(--text-subtle)' }}>No description available.</span>}</p>
                      )}
                    </div>

                    {/* Move-to-Live request / approval (shows only when the company
                        has Live Support enabled and the ticket is in a QA/UAT Pass). */}
                    <div className="sprint-detail-section">
                      <LiveMovePanel
                        ticketId={selectedTicket.id}
                        ticketStatusName={selectedTicket.status_name || selectedTicket.ticket_status}
                      />
                    </div>
                  </>
                )}

                {activeTab === 'Story & Criteria' && (() => {
                  const art = selectedTicket.artifact;
                  const artNorm = art?.normalized || {};
                  const artDevSpec = art?.dev_spec || {};

                  // User story: artifact first → ticket fallback
                  const artUS = artNorm.user_story || artNorm.user_stories || (Array.isArray(artNorm.user_flows) && artNorm.user_flows.length > 0 ? artNorm.user_flows : null);
                  const userStory = artUS || selectedTicket.user_story;

                  // Acceptance criteria: artifact (dev_spec or normalized) → ticket fallback
                  const artACList = artDevSpec.acceptance_criteria || artNorm.acceptance_criteria || [];
                  const hasArtAC = Array.isArray(artACList) && artACList.length > 0 && typeof artACList[0] === 'object';

                  // Plain-text versions for the copy buttons (lists one item per line).
                  const userStoryText = !userStory ? '' : typeof userStory === 'string' ? userStory
                    : Array.isArray(userStory) ? userStory.map(s => String(s)).join('\n') : String(userStory);
                  const acText = hasArtAC
                    ? artACList.filter(ac => ac && typeof ac === 'object')
                      .map((ac, i) => `${ac.id || i + 1}. Given ${ac.given || '—'} | When ${ac.when || '—'} | Then ${ac.then || '—'}`).join('\n')
                    : selectedTicket.acceptance_criteria || '';

                  return (
                  <>
                    <div className="sprint-detail-section">
                      <div className="l3-section-head">
                        <h4>User Story</h4>
                        {!isInlineEditing && userStoryText && <CopyButton text={userStoryText} label="Copy" title="Copy user story" />}
                      </div>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.user_story || ''} onChange={e => setEditedTicket(p => ({ ...p, user_story: e.target.value }))} placeholder="Write user story..." rows={5} />
                      ) : userStory ? (
                        typeof userStory === 'string' ? <p style={{ whiteSpace: 'pre-wrap' }}>{userStory}</p> :
                        Array.isArray(userStory) ? <ul className="l3-artifact-list" style={{ paddingLeft: 18 }}>{userStory.map((s, i) => <li key={i}>{String(s)}</li>)}</ul> :
                        <p>{String(userStory)}</p>
                      ) : <p style={{ color: 'var(--text-subtle)' }}>Not provided</p>}
                    </div>
                    <div className="sprint-detail-section">
                      <div className="l3-section-head">
                        <h4>Acceptance Criteria</h4>
                        {!isInlineEditing && acText && <CopyButton text={acText} label="Copy" title="Copy acceptance criteria" />}
                      </div>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.acceptance_criteria || ''} onChange={e => setEditedTicket(p => ({ ...p, acceptance_criteria: e.target.value }))} placeholder="Write acceptance criteria..." rows={5} />
                      ) : hasArtAC ? (
                        <table className="l3-test-table">
                          <thead><tr><th>ID</th><th>Given</th><th>When</th><th>Then</th></tr></thead>
                          <tbody>
                            {artACList.filter(ac => ac && typeof ac === 'object').map((ac, i) => (
                              <tr key={ac.id || i}><td>{ac.id || i + 1}</td><td>{ac.given || '—'}</td><td>{ac.when || '—'}</td><td>{ac.then || '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      ) : selectedTicket.acceptance_criteria ? (
                        <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.acceptance_criteria}</p>
                      ) : <p style={{ color: 'var(--text-subtle)' }}>Not provided</p>}
                    </div>
                  </>
                  );
                })()}

                {activeTab === 'Attachments' && (
                  <div className="sprint-detail-section">
                    {/* Upload: drop, paste (Ctrl+V) or pick — each uploads straight away */}
                    <div
                      className={`att-drop${attDragOver ? ' att-drop--over' : ''}`}
                      onDragOver={e => { e.preventDefault(); if (!attDragOver) setAttDragOver(true); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setAttDragOver(false); }}
                      onDrop={e => { e.preventDefault(); setAttDragOver(false); uploadFiles(e.dataTransfer.files); }}
                    >
                      <FiUploadCloud size={22} className="att-drop__icon" />
                      <div className="att-drop__text">
                        <span><strong>Drop files here</strong> or paste (Ctrl+V)</span>
                        <span className="att-drop__hint">{ATTACHMENT_EXTENSIONS.map(x => `.${x}`).join(', ')} · max {ATTACHMENT_MAX_MB}MB each</span>
                      </div>
                      <button className="fd-btn-primary att-drop__browse" onClick={() => fileInputRef.current?.click()}>
                        <FiPaperclip size={13} /> Attach files
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        accept={ATTACHMENT_EXTENSIONS.map(x => `.${x}`).join(",")}
                        onChange={e => { uploadFiles(e.target.files); e.target.value = ""; }}
                      />
                    </div>
                    {skippedFiles.length > 0 && (
                      <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--status-red)', background: 'rgba(244, 67, 54, 0.08)', fontSize: '0.8rem', color: 'var(--text-primary)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <strong style={{ color: 'var(--status-red)' }}>{skippedFiles.length} file{skippedFiles.length !== 1 ? 's' : ''} not added</strong>
                          <AppTooltip title="Dismiss"><span style={{ display: 'inline-flex', cursor: 'pointer', flexShrink: 0 }} onClick={() => setSkippedFiles([])}><FiX size={14} /></span></AppTooltip>
                        </div>
                        {skippedFiles.map((s, i) => (
                          <div key={i} style={{ wordBreak: 'break-all', marginTop: 2 }}>• {s.name}: <span style={{ color: 'var(--text-subtle)' }}>{s.reason}</span></div>
                        ))}
                        <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-subtle)' }}>Allowed: {ATTACHMENT_EXTENSIONS.map(x => `.${x}`).join(', ')} · max {ATTACHMENT_MAX_MB}MB each</div>
                      </div>
                    )}
                    {/* Files — card grid; a card opens the full-screen viewer */}
                    {(() => {
                      const isImage = (att) => fileType(att.filename).kind === 'image';
                      const counts = { all: ticketAttachments.length, images: ticketAttachments.filter(isImage).length };
                      counts.docs = counts.all - counts.images;
                      const visible = ticketAttachments.filter(att => attFilter === 'all' || (attFilter === 'images') === isImage(att));
                      const uploadingNames = uploadingBatches.flatMap(b => b.names.map((n, i) => ({ key: `${b.id}-${i}`, name: n })));
                      return (
                        <>
                          <div className="att-filters">
                            {[['all', 'All'], ['images', 'Images'], ['docs', 'Documents']].map(([k, label]) => (
                              <button key={k} className={`att-filter${attFilter === k ? ' att-filter--on' : ''}`} onClick={() => setAttFilter(k)}>
                                {label} ({counts[k]})
                              </button>
                            ))}
                          </div>
                          {visible.length + uploadingNames.length > 0 ? (
                            <div className="att-grid">
                              {uploadingNames.map(u => {
                                const t = fileType(u.name);
                                return (
                                  <div key={u.key} className="att-card att-card--uploading">
                                    <div className="att-card__thumb">
                                      <span className="att-card__badge" style={{ background: t.color }}>{t.label}</span>
                                      <span className="att-card__progress"><CircularProgress size={22} /></span>
                                    </div>
                                    <div className="att-card__info">
                                      <span className="att-card__name">{u.name}</span>
                                      <span className="att-card__meta">Uploading…</span>
                                    </div>
                                  </div>
                                );
                              })}
                              {visible.map((att, i) => {
                                const name = displayName(att.filename);
                                return (
                                  <div key={att.id} className="att-card" role="button" tabIndex={0}
                                    onClick={() => openAttachmentViewer(visible, i)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAttachmentViewer(visible, i); } }}>
                                    <div className="att-card__thumb">
                                      <FileThumb name={att.filename} url={att.url} getUrl={() => getAttachmentUrl(att)} cacheKey={`att-${att.id}`} />
                                    </div>
                                    <div className="att-card__info">
                                      <TitleTooltip title={name}><span className="att-card__name">{name}</span></TitleTooltip>
                                      <span className="att-card__meta">{fileMeta(att.created_by, att.created_time) || '—'}</span>
                                    </div>
                                    <div className="att-card__actions">
                                      <AppTooltip title="Download">
                                        <button onClick={e => { e.stopPropagation(); handleDownload(att); }} aria-label="Download"><FiDownload size={13} /></button>
                                      </AppTooltip>
                                      {/* An API without can_delete (not yet deployed) still lets anyone delete. */}
                                      {att.can_delete !== false && (
                                        <AppTooltip title="Delete">
                                          <button className="att-card__del" onClick={e => { e.stopPropagation(); handleDeleteAttachment(att); }} aria-label="Delete"><FiTrash2 size={13} /></button>
                                        </AppTooltip>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="att-empty">{counts.all ? 'No files of this type' : 'No files yet'}</p>
                          )}
                        </>
                      );
                    })()}

                    {/* Links section */}
                    <h4 style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: 16, marginBottom: 6, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Links
                      <button className="fd-btn-outline" onClick={() => setShowAddLink(!showAddLink)} style={{ fontSize: '0.6rem', padding: '2px 6px' }}>
                        {showAddLink ? 'Cancel' : <><FiPlus size={10} /> Add Link</>}
                      </button>
                    </h4>
                    {showAddLink && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: '0.62rem', color: 'var(--text-subtle)', display: 'block', marginBottom: 2 }}>URL</label>
                          <input type="text" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." style={{ width: '100%', padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 120 }}>
                          <label style={{ fontSize: '0.62rem', color: 'var(--text-subtle)', display: 'block', marginBottom: 2 }}>Label</label>
                          <input type="text" value={linkName} onChange={(e) => setLinkName(e.target.value)} placeholder="Link name" style={{ width: '100%', padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} />
                        </div>
                        <button className="fd-btn-outline" disabled={!linkUrl || !linkName} onClick={handleAddLink} style={{ fontSize: '0.65rem', padding: '5px 10px' }}>
                          <FiCheck size={11} /> Add
                        </button>
                      </div>
                    )}
                    {selectedTicket?.links?.length > 0 ? (
                      <div className="sprint-detail-attachments">
                        {selectedTicket.links.map(link => (
                          <span key={link.id} className="sprint-detail-att-chip" style={{ cursor: 'default' }}>
                            <FiLink size={11} />
                            <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '0.68rem' }}>{link.url_name}</a>
                            <AppTooltip title="Delete"><span style={{ display: 'inline-flex', cursor: 'pointer', color: 'var(--danger, #e74c3c)' }} onClick={() => handleDeleteLink(link)}><FiTrash2 size={11} /></span></AppTooltip>
                          </span>
                        ))}
                      </div>
                    ) : <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)' }}>No links</p>}
                  </div>
                )}

                {activeTab === 'Branches' && (
                  <div className="sprint-detail-section">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button className="fd-btn-outline" onClick={() => { setShowAddBranch(!showAddBranch); setAddBranchForm({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' }); }} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>
                        <FiGitBranch size={11} /> {showAddBranch ? 'Cancel' : 'Add Branch'}
                      </button>
                    </div>
                    {showAddBranch && (
                      <div style={{ padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <select style={{ flex: 1, minWidth: 120, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.repository_id} onChange={(e) => setAddBranchForm(p => ({ ...p, repository_id: e.target.value }))}>
                            <option value="">Select Repository</option>
                            {gitlabRepos.filter(r => r.is_active).map(r => <option key={r.id} value={r.id}>{r.repo_name} ({r.repo_type})</option>)}
                          </select>
                          <select style={{ minWidth: 100, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.developer_id} onChange={(e) => setAddBranchForm(p => ({ ...p, developer_id: e.target.value }))}>
                            <option value="">Developer</option>
                            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="text" style={{ flex: 1, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.branch_name} onChange={(e) => setAddBranchForm(p => ({ ...p, branch_name: e.target.value }))} placeholder="Branch name" />
                          <select style={{ minWidth: 80, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.branch_type} onChange={(e) => setAddBranchForm(p => ({ ...p, branch_type: e.target.value }))}>
                            <option value="feature">Feature</option><option value="bugfix">Bugfix</option><option value="hotfix">Hotfix</option><option value="release">Release</option><option value="other">Other</option>
                          </select>
                          <button className="fd-btn-primary" onClick={handleAddBranch} style={{ fontSize: '0.65rem', padding: '4px 10px' }}>Add</button>
                        </div>
                      </div>
                    )}
                    {ticketBranches.length > 0 ? ticketBranches.map(br => {
                      const syncData = br.last_sync_data || {};
                      const isExpBr = expandedBranchId === br.id;
                      const commits = syncData.commits || [];
                      const mrs = syncData.merge_requests || [];
                      const changedFiles = syncData.changed_files || [];
                      const fmtDT = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

                      return (
                      <div key={br.id} className="l3-branch-card">
                        <div className="l3-branch-card__header">
                          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setExpandedBranchId(isExpBr ? null : br.id)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {isExpBr ? <FiChevronDown size={12} style={{ color: 'var(--text-muted)' }} /> : <FiChevronRight size={12} style={{ color: 'var(--text-muted)' }} />}
                              <FiGitBranch size={12} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-primary)' }}>{br.branch_name}</span>
                              <span className={`fd-status-pill ${br.merge_status === 'merged' ? 'fd-status-pill--success' : br.merge_status === 'open' ? 'fd-status-pill--info' : 'fd-status-pill--muted'}`}>
                                {br.merge_status === 'merged' ? 'Merged' : br.merge_status === 'open' ? 'Open MR' : br.merge_status === 'closed' ? 'Closed' : 'No MR'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.64rem', color: 'var(--text-subtle)', marginTop: 2, paddingLeft: 20 }}>
                              {br.repo_name} ({br.repo_type}) {br.developer_name ? `• ${br.developer_name}` : ''}{br.total_commits > 0 && ` • ${br.total_commits} commits`}{br.files_changed > 0 && ` • ${br.files_changed} files (+${br.additions || 0}/-${br.deletions || 0})`}{br.target_branch && br.merge_status === 'merged' && ` → ${br.target_branch}`}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            <AppTooltip title="Sync">
                              <span style={{ display: 'inline-flex' }}>
                                <button className="fd-action-btn" onClick={() => handleSyncBranch(br.id)} disabled={branchSyncing === br.id}>{branchSyncing === br.id ? <CircularProgress size={11} /> : <FiRefreshCw size={10} />}</button>
                              </span>
                            </AppTooltip>
                            <AppTooltip title="Remove"><button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDeleteBranch(br.id)}><FiTrash2 size={10} /></button></AppTooltip>
                          </div>
                        </div>
                        {/* Expanded: Full Branch Lifecycle (matches Branch Audit Report) */}
                        {isExpBr && (() => {
                          const mainMR = mrs.length > 0 ? mrs[0] : null;
                          const fmtDTFull = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return d; } };

                          // Build timeline events
                          const events = [];
                          const commitDates = commits.map(c => c.date).filter(Boolean).map(d => new Date(d).getTime());
                          const earliestCommit = commitDates.length > 0 ? new Date(Math.min(...commitDates)).toISOString() : null;

                          events.push({ type: 'created', color: '#4caf50', title: `Branch created: ${br.branch_name}`, detail: `Repository: ${br.repo_name} (${br.repo_type})${br.developer_name ? ` • Developer: ${br.developer_name}` : ''}`, time: earliestCommit || br.created_at_gitlab || br.created_time });

                          commits.forEach(c => {
                            events.push({ type: 'commit', color: 'var(--accent)', title: c.message, detail: `${c.short_sha} by ${c.author}`, time: c.date });
                          });

                          if (mainMR && mainMR.created_at) {
                            events.push({ type: 'mr_opened', color: '#2196f3', title: `MR #${mainMR.iid} opened: ${mainMR.title}`, detail: `Target: ${mainMR.target_branch}${syncData.mr_reviewers?.length ? ` • Reviewers: ${syncData.mr_reviewers.join(', ')}` : ''}${syncData.mr_pipeline_status ? ` • Pipeline: ${syncData.mr_pipeline_status}` : ''}`, time: mainMR.created_at });
                          }

                          if (br.merge_status === 'merged' && br.merged_at) {
                            events.push({ type: 'merged', color: '#4caf50', title: `Merged into ${br.target_branch}`, detail: `Merged by ${br.merged_by || 'unknown'}${br.files_changed > 0 ? ` • ${br.files_changed} files changed (+${br.additions}/-${br.deletions})` : ''}`, time: br.merged_at });
                          }

                          if (br.merge_status === 'closed') {
                            events.push({ type: 'closed', color: '#f44336', title: 'MR closed without merge', detail: `Target was: ${br.target_branch || '-'}`, time: br.last_synced_at });
                          }

                          events.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

                          return (
                          <div className="l3-branch-audit">
                            {/* Stats bar — matching audit report */}
                            <div className="l3-branch-stats">
                              <div><span className="l3-branch-stats__label">Status</span><span className="l3-branch-stats__val"><span className={`fd-status-pill ${br.merge_status === 'merged' ? 'fd-status-pill--success' : br.merge_status === 'open' ? 'fd-status-pill--info' : 'fd-status-pill--muted'}`}>{br.merge_status === 'merged' ? 'Merged' : br.merge_status === 'open' ? 'Open MR' : br.merge_status === 'closed' ? 'Closed' : 'No MR'}</span></span></div>
                              <div><span className="l3-branch-stats__label">Commits</span><span className="l3-branch-stats__val">{br.total_commits || 0}</span></div>
                              <div><span className="l3-branch-stats__label">Files</span><span className="l3-branch-stats__val">{br.files_changed > 0 ? `${br.files_changed} (+${br.additions || 0}/-${br.deletions || 0})` : '-'}</span></div>
                              {br.target_branch && <div><span className="l3-branch-stats__label">Target Branch</span><span className="l3-branch-stats__val" style={{ color: '#4caf50' }}>{br.target_branch}</span></div>}
                              {mainMR && <div><span className="l3-branch-stats__label">MR</span><span className="l3-branch-stats__val">#{mainMR.iid} — {mainMR.title || '-'}</span></div>}
                              <div><span className="l3-branch-stats__label">Last Synced</span><span className="l3-branch-stats__val">{fmtDTFull(br.last_synced_at)}</span></div>
                            </div>

                            {/* Branch Lifecycle Timeline */}
                            <h5 className="l3-audit-title"><FiClock size={11} /> Branch Lifecycle Timeline ({events.length} events)</h5>
                            <div className="l3-timeline">
                              {events.map((evt, ei) => (
                                <div key={ei} className="l3-timeline__item">
                                  <div className="l3-timeline__dot-col">
                                    <div className="l3-timeline__dot" style={{ background: evt.color }} />
                                    {ei < events.length - 1 && <div className="l3-timeline__line" />}
                                  </div>
                                  <div className="l3-timeline__content">
                                    <div className="l3-timeline__title">{evt.title}</div>
                                    {evt.detail && <div className="l3-timeline__detail">{evt.detail}</div>}
                                  </div>
                                  <div className="l3-timeline__time">{fmtDTFull(evt.time)}</div>
                                </div>
                              ))}
                            </div>

                            {/* Changed files */}
                            {changedFiles.length > 0 && (
                              <>
                                <h5 className="l3-audit-title"><FiFileText size={11} /> Changed Files ({changedFiles.length})</h5>
                                <div className="l3-files-list">
                                  {changedFiles.map((f, fi) => (
                                    <div key={fi} className="l3-file-row">
                                      <span className={`l3-file-badge ${f.new_file ? 'l3-file-badge--add' : f.deleted ? 'l3-file-badge--del' : f.renamed ? 'l3-file-badge--ren' : 'l3-file-badge--mod'}`}>
                                        {f.new_file ? 'A' : f.deleted ? 'D' : f.renamed ? 'R' : 'M'}
                                      </span>
                                      <span className="l3-file-path">{f.new_path || f.old_path}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          );
                        })()}
                      </div>
                      );
                    }) : <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)' }}>No branches tracked</p>}
                  </div>
                )}

                {/* ── Test Cases tab (Quality module) ── */}
                {activeTab === 'Test Cases' && qualityEnabled && (
                  <TicketTestCasesTab
                    key={selectedTicket.id}
                    ticketId={selectedTicket.id}
                    ticketCode={tCode}
                    moduleName={selectedTicket.module_name || ''}
                    onCount={setTestCaseCount}
                  />
                )}

                {/* ── Stories tab (Epic tickets) ── */}
                {activeTab === 'Stories' && isEpic && (
                  <div className="l3-child-tickets">
                    <div className="l3-child-tickets__header">
                      <h4><FiGitBranch size={13} /> Story Tickets ({childTickets.length})</h4>
                    </div>
                    {childTickets.map(child => {
                      const cStatus = child.status_name || child.ticket_status || '-';
                      const cCode = child.child_code || child.parent_code;
                      return (
                        <div key={child.id} className="l3-child-ticket" onClick={() => handleChildTicketClick(child, 'Stories')}>
                          <span className="sl2-type-badge sl2-type-badge--story">Story</span>
                          <span className="l3-child-ticket__code">{cCode}</span>
                          <TitleTooltip title={child.short_description}><span className="l3-child-ticket__desc">{child.short_description || '-'}</span></TitleTooltip>
                          <TitleTooltip title={`${cStatus} · click to change`}><span className={`fd-status-pill l3-status-click ${/done|completed|live|production/i.test(cStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(cStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(cStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`} role="button" tabIndex={0} onClick={e => openStatusDrop(e, child.id)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openStatusDrop(e, child.id); }}>{cStatus}</span></TitleTooltip>
                          <span className="l3-child-ticket__prio">{child.priority_name || '-'}</span>
                          {child.bug_tickets?.length > 0 && <span className="sl2-bug-chip"><GoBug size={10} /> {child.bug_tickets.length}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Bugs tab (Story tickets) ── */}
                {activeTab === 'Bugs' && isStory && (
                  <div className="l3-child-tickets">
                    <div className="l3-child-tickets__header l3-child-tickets__header--action">
                      <h4>
                        {bugTickets.length > 0 && (
                          <input type="checkbox" className="l3-bug-check" checked={allBugsPicked} aria-label="Select all bugs"
                            onChange={() => setSelectedBugIds(allBugsPicked ? [] : bugTickets.map(b => b.id))} />
                        )}
                        <GoBug size={13} /> Bug Tickets ({bugTickets.length})
                      </h4>
                      {isParentStory && (
                        <button className="l3-edit-btn" onClick={() => openAddTicketPopup(selectedTicket, 'Bug')}><FiPlus size={13} /> Add Bug</button>
                      )}
                    </div>
                    {bugTickets.length === 0 && (
                      <div className="l3-child-tickets__empty">No bugs yet. Use "Add Bug" to raise one against this story.</div>
                    )}
                    {bugTickets.map(bug => {
                      const bStatus = bug.status_name || bug.ticket_status || '-';
                      const bCode = bug.bug_code;
                      return (
                        <div key={bug.id} className={`l3-child-ticket l3-child-ticket--bug${selectedBugIds.includes(bug.id) ? ' l3-child-ticket--picked' : ''}`} onClick={() => handleChildTicketClick(bug, 'Bugs')}>
                          <input type="checkbox" className="l3-bug-check" checked={selectedBugIds.includes(bug.id)} aria-label={`Select ${bug.bug_code || 'bug'}`}
                            onClick={e => e.stopPropagation()} onChange={() => toggleBug(bug.id)} />
                          <span className="sl2-type-badge sl2-type-badge--bug">Bug</span>
                          <span className="l3-child-ticket__code">{bCode}</span>
                          <TitleTooltip title={bug.short_description}><span className="l3-child-ticket__desc">{bug.short_description || '-'}</span></TitleTooltip>
                          <TitleTooltip title={`${bStatus} · click to change`}><span className={`fd-status-pill l3-status-click ${/done|completed|live|production/i.test(bStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(bStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(bStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`} role="button" tabIndex={0} onClick={e => openStatusDrop(e, bug.id)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openStatusDrop(e, bug.id); }}>{bStatus}</span></TitleTooltip>
                          <AppTooltip title="Change bug type">
                            <span className="l3-child-ticket__bugtype" role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={e => openBugTypeMenu(e, bug)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openBugTypeMenu(e, bug); }}>{bug.bug_type || 'Set type'}</span>
                          </AppTooltip>
                          <span className="l3-child-ticket__prio">{bug.priority_name || '-'}</span>
                          <AppTooltip title={isInProduction(bug) ? MOVE_BLOCKED_HINT : "Move to Sprint"}>
                            <span style={{ display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
                            <button
                              className="fd-action-btn"
                              onClick={e => { e.stopPropagation(); openMoveBug(bug); }}
                              disabled={isInProduction(bug)}
                            >
                              <FiGitMerge size={12} />
                            </button>
                            </span>
                          </AppTooltip>
                          <AppTooltip title="Delete bug">
                            <button
                              className="fd-action-btn fd-action-btn--danger"
                              onClick={e => { e.stopPropagation(); handleDeleteTicket(bug.id); }}
                            >
                              <FiTrash2 size={12} />
                            </button>
                          </AppTooltip>
                        </div>
                      );
                    })}
                    {pickedBugs.length > 0 && (
                      <div className="l3-bulk-bar">
                        <span className="l3-bulk-bar__count">{pickedBugs.length} selected</span>
                        <button className="fd-btn-primary" onClick={() => setBulkStatus({ bugs: pickedBugs, statusId: '' })}>Change status</button>
                        <button className="fd-btn-outline" onClick={() => setSelectedBugIds([])}>Clear</button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Artifact sub-tabs: Normalized, Dev Spec, QA Plan ── */}
                {hasArtifact && (() => {
                  const art = selectedTicket.artifact;

                  // Render a list of items (strings or objects)
                  const renderList = (arr) => {
                    if (!arr || !Array.isArray(arr) || arr.length === 0) return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
                    return (
                      <ul className="l3-artifact-list">
                        {arr.map((item, i) => <li key={i}>{typeof item === 'object' ? (item.given ? `Given ${item.given}, When ${item.when}, Then ${item.then}` : Object.values(item).join(' — ')) : String(item)}</li>)}
                      </ul>
                    );
                  };

                  // Render sections: skip test_cases/unit_tests (handled separately as tables)
                  const renderSections = (data, skipKeys = []) => {
                    if (!data || typeof data !== 'object') return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    return Object.entries(data).filter(([k]) => !skipKeys.includes(k)).map(([key, val]) => (
                      <div key={key} className="l3-artifact-section">
                        <h4 className="l3-artifact-section__title">{key.replace(/_/g, ' ')}</h4>
                        <div style={{ padding: '10px 16px' }}>
                          {Array.isArray(val) ? renderList(val) : typeof val === 'object' && val ? (
                            Object.entries(val).map(([k2, v2]) => (
                              <div key={k2} className="l3-artifact-item">
                                <span className="l3-artifact-item__key">{k2.replace(/_/g, ' ')}</span>
                                <div className="l3-artifact-item__val">{Array.isArray(v2) ? renderList(v2) : <span>{String(v2)}</span>}</div>
                              </div>
                            ))
                          ) : <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{String(val)}</span>}
                        </div>
                      </div>
                    ));
                  };

                  // Test cases table renderer (for both QA test cases and unit tests)
                  const renderTestTable = (cases, title) => {
                    if (!cases || !Array.isArray(cases) || cases.length === 0) return null;
                    // Detect columns from first item
                    const cols = Object.keys(cases[0]).filter(k => !['test_data', 'maps_to_acceptance_criteria'].includes(k));
                    return (
                      <div className="l3-artifact-section">
                        <h4 className="l3-artifact-section__title">{title} ({cases.length})</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="l3-test-table">
                            <thead>
                              <tr>{cols.map(c => <th key={c}>{c.replace(/_/g, ' ')}</th>)}</tr>
                            </thead>
                            <tbody>
                              {cases.map((tc, i) => (
                                <tr key={i}>
                                  {cols.map(c => (
                                    <td key={c}>
                                      {Array.isArray(tc[c]) ? tc[c].join(', ') : typeof tc[c] === 'object' && tc[c] ? JSON.stringify(tc[c]) : String(tc[c] ?? '—')}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  };

                  if (activeTab === 'Normalized') {
                    const norm = art.normalized;
                    if (!norm) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    return renderSections(norm, ['test_cases', 'unit_tests']);
                  }

                  if (activeTab === 'Dev Spec') {
                    const spec = art.dev_spec;
                    if (!spec) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    const unitTestsObj = spec.unit_tests || art.unit_tests;
                    // unit_tests can be: { unit_test_cases: [...], test_framework_suggestion: "..." } or just an array
                    const unitTestCases = Array.isArray(unitTestsObj) ? unitTestsObj : (unitTestsObj?.unit_test_cases || unitTestsObj?.test_cases || unitTestsObj?.cases || []);
                    const testFramework = unitTestsObj?.test_framework_suggestion || null;
                    return (
                      <>
                        {renderSections(spec, ['unit_tests', 'acceptance_criteria'])}
                        {unitTestCases.length > 0 && (
                          <div className="l3-artifact-section">
                            <h4 className="l3-artifact-section__title">Unit Tests ({unitTestCases.length}){testFramework && <span style={{ fontWeight: 400, fontSize: '0.65rem', marginLeft: 8, color: 'var(--text-muted)' }}>Framework: {testFramework}</span>}</h4>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="l3-test-table">
                                <thead>
                                  <tr><th>ID</th><th>Title</th><th>Priority</th><th>Target</th><th>Description</th><th>Expected Behavior</th><th>Assertions</th></tr>
                                </thead>
                                <tbody>
                                  {unitTestCases.filter(ut => ut && typeof ut === 'object').map((ut, i) => (
                                    <tr key={ut.id || i}>
                                      <td>{ut.id || i + 1}</td>
                                      <td>{ut.title || '—'}</td>
                                      <td>{ut.priority || '—'}</td>
                                      <td>{ut.target || '—'}</td>
                                      <td>{ut.description || '—'}</td>
                                      <td>{ut.expected_behavior || '—'}</td>
                                      <td>{Array.isArray(ut.assertions) ? ut.assertions.join(', ') : (ut.assertions || '—')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  if (activeTab === 'QA Plan') {
                    const qa = art.qa_plan;
                    if (!qa) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    const testCases = Array.isArray(qa.test_cases) ? qa.test_cases : (Array.isArray(qa.cases) ? qa.cases : []);
                    return (
                      <>
                        {renderSections(qa, ['test_cases', 'cases'])}
                        {testCases.length > 0 && (
                          <div className="l3-artifact-section">
                            <h4 className="l3-artifact-section__title">Test Cases ({testCases.length})</h4>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="l3-test-table">
                                <thead>
                                  <tr><th>ID</th><th>Title</th><th>Type</th><th>Priority</th><th>Preconditions</th><th>Steps</th><th>Expected Results</th></tr>
                                </thead>
                                <tbody>
                                  {testCases.filter(tc => tc && typeof tc === 'object').map((tc, i) => (
                                    <tr key={tc.id || i}>
                                      <td>{tc.id || i + 1}</td>
                                      <td>{tc.title || '—'}</td>
                                      <td><span className={`l3-tc-type ${tc.type === 'positive' ? 'l3-tc-type--pos' : tc.type === 'negative' ? 'l3-tc-type--neg' : ''}`}>{tc.type || '—'}</span></td>
                                      <td>{tc.priority || '—'}</td>
                                      <td>{Array.isArray(tc.preconditions) ? tc.preconditions.join('; ') : (tc.preconditions || '—')}</td>
                                      <td>{Array.isArray(tc.steps) ? <ol style={{ margin: 0, paddingLeft: 16 }}>{tc.steps.map((s, si) => <li key={si}>{s}</li>)}</ol> : (tc.steps || '—')}</td>
                                      <td>{Array.isArray(tc.expected_results) ? tc.expected_results.join('; ') : (tc.expected_results || '—')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  return null;
                })()}
              </div>
            </div>

            {/* Comments & Timeline Panel */}
            <CommentsPanel
              comments={ticketComments}
              loading={commentsLoading}
              onAddComment={handleAddComment}
              onEditComment={handleEditComment}
              onOpenFiles={openCommentFiles}
              employees={employees}
              statusLog={selectedTicket.status_log || []}
              sprintMoves={selectedTicket.sprint_move_log || []}
              ticketCreatedTime={selectedTicket.created_time}
              ticketCreatedBy={selectedTicket.created_by}
              branches={ticketBranches}
            />
          </div>
        </>
        );
      })()}

      {/* ── Bug Type Dropdown (same look/portal as the status dropdown) ── */}
      {bugTypeMenu && createPortal(
        <div className="sl2-status-edit" style={{ top: bugTypeMenu.top, left: bugTypeMenu.left }} onClick={e => e.stopPropagation()}>
          {BUG_TYPES.map(bt => (
            <div key={bt} className={`sl2-status-option ${bugTypeMenu.ticket.bug_type === bt ? 'sl2-status-option--current' : ''}`} onClick={() => handleBugTypeChange(bugTypeMenu.ticket, bt)}>
              {bt}
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* ── Context Menu ── */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeContextMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {/* Hierarchy: Epic → Story only · Story → Bug only · Bug → no children */}
        {menuTicket?.ticket_type === 'Epic' && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => openAddTicketPopup(menuTicket, 'Story')}><FiPlus size={12} style={{ marginRight: 6 }} />Add Story</MenuItem>
        )}
        {menuTicket?.ticket_type === 'Story' && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => openAddTicketPopup(menuTicket, 'Bug')}><GoBug size={12} style={{ marginRight: 6 }} />Add Bug</MenuItem>
        )}
        {(menuTicket?.ticket_type === 'Epic' || menuTicket?.story_type === 'Parent') && (
          <MenuItem sx={{ fontSize: '0.75rem' }} disabled={isInProduction(menuTicket)} onClick={() => { setMovePopup(true); closeContextMenu(); }}>
            <FiGitMerge size={12} style={{ marginRight: 6 }} />Move to Sprint{isInProduction(menuTicket) ? ' (in Production)' : ''}
          </MenuItem>
        )}
        {liveSupportEnabled && selectedSprint?.is_live_support && userPermission?.includes('Request Live Move') && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => handleSendToLive(menuTicket)}><FiGitMerge size={12} style={{ marginRight: 6 }} />Send to Live</MenuItem>
        )}
        {userPermission?.includes('Delete Ticket') && (
          <MenuItem sx={{ fontSize: '0.75rem', color: 'var(--error, #e53935)' }} onClick={() => { handleDeleteTicket(menuTicket?.id); closeContextMenu(); }}><FiTrash2 size={12} style={{ marginRight: 6 }} />Delete</MenuItem>
        )}
      </Menu>

      {/* ── Detailed View Modal ── */}
      {showDetailedView && detailViewTicket && (
        <DetailedTicketView
          onClose={() => { setShowDetailedView(false); setDetailViewTicket(null); }}
          ticketId={detailViewTicket.id}
        />
      )}

      {/* ── Move to Sprint Modal ── */}
      {movePopup && (
        <div className="popup-overlay">
          <div className="popup-content" style={{ maxWidth: 400 }}>
            <h3 style={{ marginBottom: 12 }}>{menuTicket?.ticket_type === 'Bug' ? `Move Bug ${menuTicket.bug_code || ''} to Sprint` : 'Move Ticket to Sprint'}</h3>
            {menuTicket?.ticket_type === 'Bug' && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-subtle)', marginBottom: 10 }}>
                This bug becomes a Story in the selected sprint with a new code, and bugs can be added under it. It leaves its current story; comments, attachments and history stay with it.
              </p>
            )}
            {menuTicket && menuTicket.ticket_type !== 'Bug' && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-subtle)', marginBottom: 10 }}>
                Its child stories and the bugs in this sprint move with it. Bugs already moved to another sprint stay there.
              </p>
            )}
            <select className="fd-search" style={{ width: '100%', maxWidth: 'none', marginBottom: 12 }} value={moveTarget || ''} onChange={(e) => setMoveTarget(e.target.value)}>
              <option value="" disabled>Select Sprint</option>
              {allSprints.filter(s => s.id !== selectedSprint?.id).map(s => <option key={s.id} value={s.id}>{s.sprint_name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="fd-btn-outline" onClick={() => { setMovePopup(false); setMenuTicket(null); }}>Cancel</button>
              <button className="fd-btn-primary" onClick={handleMoveTicket} disabled={!moveTarget || moveSaving}>{moveSaving ? 'Moving...' : 'Move'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Story → a cascade status (In QA ... Production): bugs that follow it ── */}
      {cascadePopup && (() => {
        const { ticket, statusName, rows } = cascadePopup;
        const offered = rows.filter(r => r.pick !== 'lock');
        const locked = rows.filter(r => r.pick === 'lock');
        const toggle = (id) => setCascadeChecked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
        return (
          <div className="popup-overlay">
            <div className="popup-content sl2-cascade" style={{ maxWidth: 560 }}>
              <h3>{ticket.parent_code || ticket.child_code} → {statusName}</h3>
              <p className="sl2-cascade__hint">Move this story's bugs to {statusName} too? Failed and not-yet-fixed bugs are unticked; tick them only if they are really done.</p>
              <div className="sl2-cascade__list">
                {offered.map(({ bug, name, pick, reason }) => (
                  <label key={bug.id} className="sl2-cascade__row">
                    <input type="checkbox" checked={cascadeChecked.includes(bug.id)} onChange={() => toggle(bug.id)} />
                    <span className="sl2-cascade__code">{bug.bug_code}</span>
                    <TitleTooltip title={bug.short_description}><span className="sl2-cascade__desc">{bug.short_description || '-'}</span></TitleTooltip>
                    <span className={`sl2-cascade__status${pick === 'warn' ? ' sl2-cascade__status--warn' : ''}`}>{pick === 'warn' && '⚠ '}{name || '-'}</span>
                    {pick === 'warn' && <span className="sl2-cascade__reason">{reason}</span>}
                  </label>
                ))}
              </div>
              {locked.length > 0 && (
                <details className="sl2-cascade__locked">
                  <summary>{locked.length} bug{locked.length > 1 ? 's' : ''} won't change</summary>
                  {locked.map(({ bug, name, reason }) => (
                    <div key={bug.id} className="sl2-cascade__row sl2-cascade__row--locked">
                      <span className="sl2-cascade__code">{bug.bug_code}</span>
                      <span className="sl2-cascade__desc">{bug.short_description || '-'}</span>
                      <span className="sl2-cascade__status">{name || '-'}</span>
                      <span className="sl2-cascade__reason">{reason}</span>
                    </div>
                  ))}
                </details>
              )}
              <div className="sl2-cascade__actions">
                <button className="fd-btn-outline" onClick={() => setCascadePopup(null)} disabled={cascadeSaving}>Cancel</button>
                <button className="fd-btn-outline" onClick={() => confirmCascade(false)} disabled={cascadeSaving}>Story only</button>
                <button className="fd-btn-primary" onClick={() => confirmCascade(true)} disabled={cascadeSaving || cascadeChecked.length === 0}>
                  {cascadeSaving ? 'Saving...' : `Story + ${cascadeChecked.length} bug${cascadeChecked.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bugs tab: change status of the selected bugs ── */}
      {bulkStatus && (() => {
        // Under release approval a bug reaches Production only with its story.
        const releaseGuardOn =
          JSON.parse(localStorage.getItem('releaseApprovalEnabled') || 'false') ||
          JSON.parse(localStorage.getItem('liveSupportEnabled') || 'false');
        const options = ticketStatuses.filter(s => s.is_active && !(releaseGuardOn && isProductionStatus(s.id)));
        return (
          <div className="popup-overlay">
            <div className="popup-content sl2-cascade" style={{ maxWidth: 440 }}>
              <h3>Change status · {bulkStatus.bugs.length} bug{bulkStatus.bugs.length > 1 ? 's' : ''}</h3>
              <select className="fd-search" style={{ width: '100%', maxWidth: 'none', margin: '10px 0' }} value={bulkStatus.statusId}
                onChange={e => setBulkStatus(p => ({ ...p, statusId: e.target.value }))}>
                <option value="" disabled>Select status</option>
                {options.map(s => <option key={s.id} value={s.id}>{s.ticket_status}</option>)}
              </select>
              <p className="sl2-cascade__hint">{bulkStatus.bugs.map(b => b.bug_code).join(', ')}</p>
              <div className="sl2-cascade__actions">
                <button className="fd-btn-outline" onClick={() => setBulkStatus(null)} disabled={bulkSaving}>Cancel</button>
                <button className="fd-btn-primary" onClick={handleBulkBugStatus} disabled={!bulkStatus.statusId || bulkSaving}>
                  {bulkSaving ? 'Updating...' : `Update ${bulkStatus.bugs.length}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Add Child/Bug Ticket Popup (Modern) ── */}
      {addTicketPopup && (
        <div className="sl2-qc-overlay" onClick={() => setAddTicketPopup(false)}>
          <div className="sl2-qc-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-qc-modal__header">
              <h2>Add {addTicketType} <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{addTicketParent ? `${addTicketType === 'Story' ? 'under' : 'for'} ${addTicketParent.parent_code || addTicketParent.child_code}` : `in ${selectedSprint?.sprint_name || 'this sprint'}`}</span></h2>
              <button className="sl2-qc-modal__close" onClick={() => setAddTicketPopup(false)}><FiX size={18} /></button>
            </div>
            <div className="sl2-qc-modal__body">
              <div className="sl2-qc-field">
                <label>Title</label>
                <input type="text" placeholder={`What does this ${addTicketType.toLowerCase()} cover?`} value={addTicketForm.short_description} onChange={(e) => setAddTicketForm(p => ({ ...p, short_description: e.target.value }))} autoFocus />
              </div>
              <div className="sl2-qc-row">
                {!addTicketParent && (
                  <div className="sl2-qc-field">
                    <label>Type</label>
                    <div className="sl2-qc-type-group">
                      {['Epic', 'Story'].map(tt => (
                        <button key={tt} className={`sl2-qc-type-btn ${addTicketType === tt ? 'sl2-qc-type-btn--active' : ''}`} onClick={() => setAddTicketType(tt)}>{tt}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="sl2-qc-field">
                  <label>Priority</label>
                  <div className="sl2-qc-prio-group">
                    {priorityTypes.filter(p => p.is_active).map(p => (
                      <button key={p.id} className={`sl2-qc-prio-btn ${String(addTicketForm.priority_id) === String(p.id) ? 'sl2-qc-prio-btn--active' : ''}`} onClick={() => setAddTicketForm(prev => ({ ...prev, priority_id: prev.priority_id === p.id ? '' : p.id }))}>{p.priority}</button>
                    ))}
                  </div>
                </div>
                <div className="sl2-qc-field">
                  <label>Due Date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                  <input type="date" value={addTicketForm.due_date || ''} onChange={e => setAddTicketForm(p => ({ ...p, due_date: e.target.value }))} />
                </div>
                {addTicketType === 'Bug' && (
                  <div className="sl2-qc-field">
                    <label>Bug Type</label>
                    <div className="sl2-qc-type-group">
                      {BUG_TYPES.map(bt => (
                        <button key={bt} className={`sl2-qc-type-btn ${addTicketForm.bug_type === bt ? 'sl2-qc-type-btn--active sl2-qc-type-btn--bug' : ''}`} onClick={() => setAddTicketForm(p => ({ ...p, bug_type: bt }))}>{bt}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="sl2-qc-status-info">
                <span className="sl2-qc-status-dot" />Status will be set to <strong>To Do</strong>
              </div>
            </div>
            <div className="sl2-qc-modal__footer">
              <button className="sl2-qc-cancel" onClick={() => setAddTicketPopup(false)}>Cancel</button>
              <button className="sl2-qc-submit" onClick={handleSaveNewTicket}>
                {addTicketType === 'Bug' ? <><GoBug size={13} /> Create Bug</> : <><FiPlus size={13} /> Create {addTicketType}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sprint Add/Edit Popup ── */}
      {showSprintPopup && (
        <div className="popup-overlay">
          <div className="popup-content-req" style={{ maxWidth: 560 }}>
            <h3>{editSprintId ? 'Edit Sprint' : 'Add Sprint'}</h3>
            <div className="req-popup-form">
              <div className="req-popup-field full-width">
                <label>Sprint Name</label>
                <input type="text" value={sprintForm.sprint_name} onChange={(e) => setSprintForm(p => ({ ...p, sprint_name: e.target.value }))} placeholder="Sprint name" />
              </div>
              <div className="req-popup-field">
                <label>Short Name</label>
                <input type="text" value={sprintForm.short_name} onChange={(e) => setSprintForm(p => ({ ...p, short_name: e.target.value }))} placeholder="e.g. DEC1" />
              </div>
              <div className="req-popup-field">
                <label>Team Name</label>
                <input type="text" value={sprintForm.team_name} onChange={(e) => setSprintForm(p => ({ ...p, team_name: e.target.value }))} placeholder="Team name" />
              </div>
              <div className="req-popup-field">
                <label>Start Date</label>
                <input type="date" value={sprintForm.start_date} onChange={(e) => setSprintForm(p => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div className="req-popup-field">
                <label>End Date</label>
                <input type="date" value={sprintForm.end_date} onChange={(e) => setSprintForm(p => ({ ...p, end_date: e.target.value }))} />
              </div>
              <div className="req-popup-field">
                <label>Status</label>
                <select
                  value={sprintForm.sprint_status || 'ONGOING'}
                  onChange={(e) => setSprintForm(p => ({ ...p, sprint_status: e.target.value }))}
                >
                  {SPRINT_STATUS_OPTIONS.filter(o => o.value !== 'all').map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="req-popup-field full-width">
                <label>Team Members</label>
                <Autocomplete
                  multiple size="small"
                  options={employees}
                  getOptionLabel={(opt) => opt.name || ''}
                  value={employees.filter(e => (sprintForm.team_members || []).includes(e.id))}
                  onChange={(_, val) => setSprintForm(p => ({ ...p, team_members: val.map(v => v.id) }))}
                  renderInput={(params) => <TextField {...params} placeholder="Select members" size="small" />}
                />
              </div>
              {liveSupportEnabled && (
                <div className="req-popup-field full-width">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!sprintForm.is_live_support}
                      onChange={(e) => setSprintForm(p => ({ ...p, is_live_support: e.target.checked }))}
                      style={{ width: "16px", height: "16px" }}
                    />
                    Live Support Team Sprint
                  </label>
                </div>
              )}
            </div>
            <div className="req-popup-actions">
              <button className="fd-btn-outline" onClick={() => { setShowSprintPopup(false); setEditSprintId(null); }}>Cancel</button>
              <button className="fd-btn-primary" onClick={handleSaveSprint}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Side Panel (existing TicketPanel) ── */}
      {isSidebarOpen && (
        <TicketSidePanel
          editData={editData}
          closePanel={handleSidePanelClose}
          sprintId={selectedSprint?.id}
          refreshData={() => setRefreshTrigger(p => !p)}
        />
      )}
    </div>
  );
};

/* ── Simple comment input component ── */
/* ── Comments Panel with Reply Support ── */
const CommentsPanel = ({ comments, loading, onAddComment, onEditComment, onOpenFiles, employees, statusLog = [], sprintMoves = [], ticketCreatedTime, ticketCreatedBy, branches = [] }) => {
  const [panelTab, setPanelTab] = useState('comments');
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [history, setHistory] = useState(null); // { comment, versions, error }
  const closeHistory = useCallback(() => setHistory(null), []);
  // Known names, so "@Name" in history is recognised as a mention.
  const employeeNames = useMemo(() => (employees || []).map(e => e.name).filter(Boolean), [employees]);
  const commentsList = Object.values(comments || {});

  // Every comment file in the order shown, so the viewer can step through them all.
  const allFiles = React.useMemo(() => {
    const out = [];
    const take = (c) => (c.attachments || []).forEach(a => out.push({ ...a, comment_id: c.id, commented_by_name: c.commented_by_name }));
    commentsList.forEach(c => { take(c); (c.sub_comments || []).forEach(take); });
    return out;
  }, [commentsList]);
  const openFile = (f) => onOpenFiles?.(allFiles, Math.max(0, allFiles.findIndex(x => x.id === f.id)));

  const openHistory = async (c) => {
    setHistory({ comment: c, versions: null });
    try {
      const data = await sprintApi.commentHistory(c.id);
      setHistory(h => (h?.comment.id === c.id ? { ...h, versions: data.status === 'success' ? data.data : [], error: data.status === 'success' ? null : data.message } : h));
    } catch {
      setHistory(h => (h?.comment.id === c.id ? { ...h, versions: [], error: 'Could not load the history' } : h));
    }
  };

  // Stored comments are mostly plain text (keep its line breaks); some carry
  // HTML, which is rendered only after sanitising.
  const renderBody = (text) => (looksLikeHtml(text)
    ? <div className="l3-comment__body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
    : <div className="l3-comment__body l3-comment__body--text">{text}</div>);
  const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
  const getColor = (name) => avatarColors[(name || '?').charCodeAt(0) % avatarColors.length];
  const fmtDT = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  // Build timeline events
  const timelineEvents = React.useMemo(() => {
    const events = [];
    // Ticket created
    if (ticketCreatedTime) {
      events.push({ type: 'created', icon: '🎫', title: 'Ticket created', detail: ticketCreatedBy ? `by ${ticketCreatedBy}` : '', time: ticketCreatedTime, color: '#4caf50' });
    }
    // Status changes
    (statusLog || []).forEach(log => {
      if (log.from_status && log.to_status) {
        events.push({ type: 'status', icon: '🔄', title: `Status changed`, detail: `${log.from_status} → ${log.to_status}`, time: log.changed_date, color: '#2196f3', by: log.changed_by });
      } else if (log.to_status) {
        events.push({ type: 'status', icon: '📋', title: `Status set to ${log.to_status}`, detail: '', time: log.changed_date, color: '#7c5cfc', by: log.changed_by });
      }
    });
    // Comments
    commentsList.forEach(c => {
      events.push({ type: 'comment', icon: '💬', title: `${c.commented_by_name || 'Someone'} commented`, detail: (c.comments || '').replace(/<[^>]*>/g, '').slice(0, 80), time: c.created_time, color: '#ff9800' });
    });
    // Sprint moves (no from-sprint = came in from Client Requirements; no to-sprint = moved back there)
    (sprintMoves || []).forEach(m => {
      events.push({ type: 'sprint', icon: '🔀', title: 'Moved sprint', detail: `${m.from_sprint_name || 'Client Requirements'} → ${m.to_sprint_name || 'Client Requirements'}`, time: m.moved_on, color: '#3f51b5', by: m.moved_by });
    });
    // Branch events
    (branches || []).forEach(br => {
      const syncData = br.last_sync_data || {};
      const commits = syncData.commits || [];
      const mrs = syncData.merge_requests || [];

      // Branch created
      const earliestCommit = commits.map(c => c.date).filter(Boolean).sort()[0];
      events.push({ type: 'branch', icon: '🌿', title: `Branch created: ${br.branch_name}`, detail: `${br.repo_name} (${br.repo_type})${br.developer_name ? ` • ${br.developer_name}` : ''}`, time: earliestCommit || br.created_at_gitlab || br.created_time, color: '#00bcd4' });

      // Commits
      commits.forEach(c => {
        events.push({ type: 'commit', icon: '📝', title: c.message, detail: `${c.short_sha} by ${c.author}`, time: c.date, color: '#8e24aa' });
      });

      // MR opened
      if (mrs[0]?.created_at) {
        events.push({ type: 'mr', icon: '🔀', title: `MR #${mrs[0].iid} opened: ${mrs[0].title}`, detail: `Target: ${mrs[0].target_branch}`, time: mrs[0].created_at, color: '#3f51b5' });
      }

      // Merged
      if (br.merge_status === 'merged' && br.merged_at) {
        events.push({ type: 'merged', icon: '✅', title: `Merged into ${br.target_branch}`, detail: `by ${br.merged_by || 'unknown'}${br.files_changed ? ` • ${br.files_changed} files` : ''}`, time: br.merged_at, color: '#4caf50' });
      }

      // MR closed
      if (br.merge_status === 'closed') {
        events.push({ type: 'closed', icon: '❌', title: 'MR closed without merge', detail: `Target: ${br.target_branch || '-'}`, time: br.last_synced_at, color: '#f44336' });
      }
    });

    events.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
    return events;
  }, [statusLog, sprintMoves, ticketCreatedTime, ticketCreatedBy, commentsList, branches]);

  const renderComment = (c, isSub = false) => (
    <div key={c.id} className={`l3-comment ${isSub ? 'l3-comment--sub' : ''}`}>
      <div className="l3-comment__head">
        <div className={`l3-comment__avatar ${isSub ? 'l3-comment__avatar--sm' : ''}`} style={{ background: getColor(c.commented_by_name) }}>
          {(c.commented_by_name || '?')[0].toUpperCase()}
        </div>
        <div className="l3-comment__info">
          <span className="l3-comment__name">{c.commented_by_name || 'Unknown'}</span>
          <span className="l3-comment__time">
            {c.time_ago || c.created_time || ''}
            {c.is_edited && (
              <AppTooltip title="See earlier versions">
                <button type="button" className="l3-comment__edited" onClick={() => openHistory(c)}>· edited</button>
              </AppTooltip>
            )}
          </span>
        </div>
        <span className="l3-comment__tools">
          {c.can_edit && editingId !== c.id && (
            <AppTooltip title="Edit comment">
              <button type="button" className="fd-copy-btn" onClick={() => setEditingId(c.id)} aria-label="Edit comment"><FiEdit2 size={12} /></button>
            </AppTooltip>
          )}
          {c.comments && <CopyButton text={() => htmlToText(c.comments)} title="Copy comment" />}
        </span>
      </div>
      {editingId === c.id ? (
        <div className="l3-comment__edit">
          <CommentInput
            initialText={c.comments || ''}
            existingFiles={c.attachments || []}
            employees={employees}
            submitLabel="Save"
            onCancel={() => setEditingId(null)}
            onSubmit={async (text, files, removedIds) => {
              const ok = await onEditComment(c.id, text, files, removedIds);
              if (ok) setEditingId(null);
              return ok;
            }}
          />
        </div>
      ) : (
        <>
          {c.comments && renderBody(c.comments)}
          {c.attachments?.length > 0 && <CommentFiles files={c.attachments.map(a => ({ ...a, comment_id: c.id }))} onOpen={openFile} />}
        </>
      )}
      {!isSub && (
        <button className="l3-comment__reply-btn" onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}>
          {replyingTo === c.id ? 'Cancel' : 'Reply'}
        </button>
      )}
      {c.sub_comments?.length > 0 && (
        <div className="l3-sub-comments">
          {c.sub_comments.map(sub => renderComment(sub, true))}
        </div>
      )}
      {replyingTo === c.id && (
        <div className="l3-reply-input">
          <CommentInput
            onSubmit={async (text, files) => { const ok = await onAddComment(text, c.id, files); if (ok) setReplyingTo(null); return ok; }}
            employees={employees} placeholder={`Reply to ${c.commented_by_name || 'comment'}...`} compact
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="l3-comments">
      {/* Tab switcher */}
      <div className="l3-panel-tabs">
        <button className={`l3-panel-tab ${panelTab === 'comments' ? 'l3-panel-tab--active' : ''}`} onClick={() => setPanelTab('comments')}>
          Comments <span className="l3-panel-tab__count">{commentsList.length}</span>
        </button>
        <button className={`l3-panel-tab ${panelTab === 'timeline' ? 'l3-panel-tab--active' : ''}`} onClick={() => setPanelTab('timeline')}>
          Timeline <span className="l3-panel-tab__count">{timelineEvents.length}</span>
        </button>
      </div>

      {/* Comments view */}
      {panelTab === 'comments' && (
        <div className="l3-comments__list">
          {loading ? <CircularProgress size={20} /> : (
            commentsList.length > 0 ? commentsList.map(c => renderComment(c)) : <p className="l3-comments__empty">No comments yet</p>
          )}
          <div className="l3-comments__input-inline">
            <CommentInput onSubmit={(text, files) => onAddComment(text, null, files)} employees={employees} />
          </div>
        </div>
      )}

      {/* Timeline view */}
      {panelTab === 'timeline' && (
        <div className="l3-timeline-panel">
          {timelineEvents.length > 0 ? timelineEvents.map((evt, i) => (
            <div key={i} className={`l3-tl-item l3-tl-item--${evt.type}`}>
              <div className="l3-tl-dot-col">
                <div className="l3-tl-dot" style={{ background: evt.color }}><span className="l3-tl-icon">{evt.icon}</span></div>
                {i < timelineEvents.length - 1 && <div className="l3-tl-line" />}
              </div>
              <div className="l3-tl-content">
                <div className="l3-tl-title">{evt.title}</div>
                {evt.detail && <div className="l3-tl-detail">{evt.detail}</div>}
                {evt.by && <div className="l3-tl-by">by {evt.by}</div>}
              </div>
              <div className="l3-tl-time">{fmtDT(evt.time)}</div>
            </div>
          )) : <p className="l3-comments__empty">No activity yet</p>}
        </div>
      )}

      {/* Edit history of one comment, newest first */}
      {history && (
        <CommentHistoryModal history={history} onClose={closeHistory} fmtDT={fmtDT} employeeNames={employeeNames} />
      )}
    </div>
  );
};

// Plain text of a comment version, for diffing (HTML comments compare as text).
const versionText = (s) => (looksLikeHtml(s || '') ? htmlToText(s) : (s || ''));

// "@Name" of a known employee shown as a mention chip inside history text.
const withMentions = (text, matcher, keyBase) => {
  if (!matcher || !text) return text;
  const out = [];
  let last = 0;
  for (const m of text.matchAll(new RegExp(matcher.source, 'g'))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={`${keyBase}-${m.index}`} className="l3-hist-mention">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const HIST_FILTERS = [['all', 'All activity'], ['text', 'Text changes'], ['files', 'Files']];

// Change-history timeline for one comment, newest first. Each entry says in one
// line what changed ("Mentioned @X", "Added 3 attachments", "Added text: …");
// the latest is expanded with a Before / After comparison, older ones are
// collapsed. Read-only. Standard modal; only the middle scrolls.
const CommentHistoryModal = ({ history, onClose, fmtDT, employeeNames }) => {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const versions = history.versions;
  const entries = React.useMemo(
    () => (versions ? buildHistoryEntries(versions, { toText: versionText, employeeNames }) : []),
    [versions, employeeNames]
  );
  const matcher = React.useMemo(() => mentionMatcher(employeeNames), [employeeNames]);
  const latestKey = entries[0]?.key;
  useEffect(() => { if (latestKey) setExpanded(new Set([latestKey])); }, [latestKey]);

  const shown = filterEntries(entries, filter);
  const counts = { all: entries.length, text: filterEntries(entries, 'text').length, files: filterEntries(entries, 'files').length };
  const toggle = (key) => setExpanded(p => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const author = history.comment?.commented_by_name;
  const day = versions?.length ? fmtDT(versions[versions.length - 1].time).split(',')[0] : '';

  const fileChip = (a, kind) => {
    const t = fileType(a.filename);
    return (
      <span key={`${kind}-${a.id}`} className={`l3-hist-file${kind ? ` l3-hist-file--${kind}` : ''}`}>
        {kind && <span className="l3-hist-file__sign" aria-label={kind === 'add' ? 'added' : 'removed'}>{kind === 'add' ? '+' : '−'}</span>}
        <span className="l3-hist-file__type" style={{ background: t.color }}>{t.label}</span>
        <span className="l3-hist-file__name">{displayName(a.filename)}</span>
      </span>
    );
  };

  const renderSide = (e, side) => {
    const runs = e.runs.filter(r => r.type !== (side === 'before' ? 'add' : 'del'));
    if (!runs.some(r => r.text.trim())) return <span className="l3-hist-none">(no text)</span>;
    return runs.map((r, k) => {
      const body = withMentions(r.text, matcher, `${e.key}-${side}-${k}`);
      if (r.type === 'add') return <ins key={k}>{body}</ins>;
      if (r.type === 'del') return <del key={k}>{body}</del>;
      return <span key={k}>{body}</span>;
    });
  };

  return createPortal(
    <div className="sl2-qc-overlay l3-hist-overlay" onClick={onClose}>
      <div className="sl2-qc-modal l3-hist-modal" role="dialog" aria-modal="true" aria-labelledby="l3-hist-title" onClick={e => e.stopPropagation()}>
        <div className="sl2-qc-modal__header l3-hist-modal__header">
          <div>
            <h2 id="l3-hist-title">Comment history</h2>
            <div className="l3-hist-modal__sub">
              {versions ? `${plural(versions.length, 'version')}${day ? ` · ${day}` : ''}` : 'Loading…'}{author ? ` · ${author}` : ''}
            </div>
          </div>
          <button type="button" className="sl2-qc-modal__close" onClick={onClose} aria-label="Close"><FiX size={18} /></button>
        </div>

        {versions && !history.error && (
          <div className="l3-hist-filters" role="tablist" aria-label="Filter history">
            {HIST_FILTERS.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={filter === k}
                className={`l3-hist-filter${filter === k ? ' l3-hist-filter--on' : ''}`} onClick={() => setFilter(k)}>
                {label} <span className="l3-hist-filter__count">{counts[k]}</span>
              </button>
            ))}
          </div>
        )}

        <div className="l3-hist-modal__body">
          {!versions ? (
            <div className="l3-hist-modal__loading"><CircularProgress size={22} /></div>
          ) : history.error ? (
            <p className="l3-hist-error">{history.error}</p>
          ) : !shown.length ? (
            <p className="l3-hist-none l3-hist-empty">{filter === 'files' ? 'No attachment changes in this comment.' : 'No text changes in this comment.'}</p>
          ) : (
            <ol className="l3-hist-timeline">
              {shown.map((e) => {
                const hasCompare = !e.isOriginal && (e.types.has('text') || e.types.has('mention'));
                const isOpen = expanded.has(e.key);
                const panelId = `l3-hist-panel-${e.key}`;
                return (
                  <li key={e.key} className={`l3-hist-item${e.isLatest ? ' l3-hist-item--latest' : ''}`}>
                    <span className="l3-hist-dot" />
                    <div className="l3-hist-card">
                      {hasCompare ? (
                        <button type="button" className="l3-hist-card__head l3-hist-card__head--btn" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggle(e.key)}>
                          <span className="l3-hist-card__title">{e.title}</span>
                          {e.isLatest && <span className="l3-hist-tag l3-hist-tag--latest">Latest</span>}
                          <span className="l3-hist-card__chev">{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</span>
                        </button>
                      ) : (
                        <div className="l3-hist-card__head">
                          <span className="l3-hist-card__title">{e.title}</span>
                          {e.isLatest && <span className="l3-hist-tag l3-hist-tag--latest">Latest</span>}
                          {e.isOriginal && <span className="l3-hist-tag">Original</span>}
                        </div>
                      )}
                      <div className="l3-hist-card__meta">{e.by || 'Unknown'} · {fmtDT(e.time)}</div>

                      {/* Only what changed */}
                      {e.isOriginal ? (
                        <>
                          <div className="l3-hist-original">{withMentions(e.after, matcher, `${e.key}-orig`) || <span className="l3-hist-none">(no text)</span>}</div>
                          {e.attachments.length > 0 && <div className="l3-hist-files">{e.attachments.map(a => fileChip(a, null))}</div>}
                        </>
                      ) : (
                        <div className="l3-hist-summary">
                          {e.addedText && <div className="l3-hist-line">Added text: <ins>“{e.addedText}”</ins></div>}
                          {e.removedText && <div className="l3-hist-line">Removed text: <del>“{e.removedText}”</del></div>}
                          {(e.mentionsAdded.length > 0 || e.mentionsRemoved.length > 0) && (
                            <div className="l3-hist-line">
                              {e.mentionsAdded.map(m => <span key={`ma-${m}`} className="l3-hist-mention l3-hist-mention--add">+ @{m}</span>)}
                              {e.mentionsRemoved.map(m => <span key={`mr-${m}`} className="l3-hist-mention l3-hist-mention--del">− @{m}</span>)}
                            </div>
                          )}
                          {(e.filesAdded.length > 0 || e.filesRemoved.length > 0) && (
                            <div className="l3-hist-files">{e.filesAdded.map(a => fileChip(a, 'add'))}{e.filesRemoved.map(a => fileChip(a, 'del'))}</div>
                          )}
                        </div>
                      )}

                      {hasCompare && isOpen && (
                        <div className="l3-hist-compare" id={panelId}>
                          <div className="l3-hist-compare__side">
                            <div className="l3-hist-compare__label">Before</div>
                            <div className="l3-hist-compare__text">{renderSide(e, 'before')}</div>
                          </div>
                          <div className="l3-hist-compare__side">
                            <div className="l3-hist-compare__label">After</div>
                            <div className="l3-hist-compare__text">{renderSide(e, 'after')}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="l3-hist-modal__footer">
          <span className="l3-hist-legend"><ins>added</ins> <del>removed</del></span>
          <button type="button" className="fd-btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const COMMENT_MAX_FILES = 5;

// Comment box: text with @mentions plus up to 5 files (pick, drop or Ctrl+V).
// Edit mode passes the comment's current text and files; removing one of those
// files is reported back through onSubmit's third argument.
// onSubmit(text, newFiles, removedFileIds) resolves true when it worked.
const CommentInput = ({ onSubmit, employees = [], placeholder, compact, initialText = "", existingFiles = [], onCancel, submitLabel = "Send" }) => {
  const [text, setText] = useState(initialText);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [cursorPos, setCursorPos] = useState(0);
  const textRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const [files, setFiles] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const keptExisting = existingFiles.filter(f => !removedIds.includes(f.id));

  const addFiles = (list) => {
    const errors = [];
    const accepted = [];
    Array.from(list || []).forEach((raw, i) => {
      // Pasted screenshots all arrive as "image.png"; give each its own name.
      const f = /^image\.(png|jpe?g)$/i.test(raw.name)
        ? new File([raw], `pasted-${new Date().toISOString().replace(/\D/g, '').slice(0, 14)}-${i + 1}.${fileExt(raw.name)}`, { type: raw.type })
        : raw;
      const ext = fileExt(f.name);
      if (!ATTACHMENT_EXTENSIONS.includes(ext)) errors.push(`${f.name}: ${ext ? `.${ext}` : 'files without an extension'} not allowed`);
      else if (f.size > ATTACHMENT_MAX_MB * 1024 * 1024) errors.push(`${f.name}: larger than ${ATTACHMENT_MAX_MB}MB`);
      else if (keptExisting.length + files.length + accepted.length >= COMMENT_MAX_FILES) errors.push(`${f.name}: a comment can have at most ${COMMENT_MAX_FILES} files`);
      else accepted.push(f);
    });
    setFileErrors(errors);
    if (accepted.length) setFiles(p => [...p, ...accepted]);
  };

  const onPaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (!pasted.length) return; // plain text paste goes into the box as usual
    e.preventDefault();
    addFiles(pasted);
  };

  const canSend = !sending && (text.trim() || files.length || keptExisting.length);
  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const ok = await onSubmit(text, files, removedIds);
    setSending(false);
    if (ok && !onCancel) { setText(""); setFiles([]); setFileErrors([]); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    setCursorPos(pos);

    // Detect @ trigger
    const textBefore = val.slice(0, pos);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx >= 0 && (atIdx === 0 || textBefore[atIdx - 1] === ' ' || textBefore[atIdx - 1] === '\n')) {
      const query = textBefore.slice(atIdx + 1);
      if (!query.includes(' ') && query.length < 20) {
        setMentionSearch(query.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (name) => {
    const textBefore = text.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const before = text.slice(0, atIdx);
    const after = text.slice(cursorPos);
    const newText = `${before}@${name} ${after}`;
    setText(newText);
    setShowMentions(false);
    setTimeout(() => {
      if (textRef.current) {
        const newPos = atIdx + name.length + 2;
        textRef.current.focus();
        textRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const filteredEmps = employees.filter(e => e.is_active !== false && (e.name || '').toLowerCase().includes(mentionSearch));

  return (
    <div
      className={`l3-comment-input-wrap${dragOver ? ' l3-comment-input-wrap--drop' : ''}`}
      onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={e => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); } }}
    >
      <div style={{ position: 'relative' }}>
        <textarea ref={textRef} className={`l3-comment-textarea ${compact ? 'l3-comment-textarea--compact' : ''}`} value={text} onChange={handleChange} onPaste={onPaste} placeholder={placeholder || "Add a comment... (@ to mention · Ctrl+V to paste a screenshot)"} rows={compact ? 1 : 2} />
        {showMentions && filteredEmps.length > 0 && (
          <div className="l3-mention-popup">
            {filteredEmps.slice(0, 6).map(emp => (
              <div key={emp.id} className="l3-mention-item" onClick={() => insertMention(emp.name)}>
                <span className="l3-mention-avatar">{(emp.name || '?')[0].toUpperCase()}</span>
                <span>{emp.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {(keptExisting.length > 0 || files.length > 0) && (
        <div className="l3-comment-chips">
          {keptExisting.map(f => (
            <span key={`old-${f.id}`} className="l3-comment-chip">
              <span className="l3-comment-chip__type" style={{ background: fileType(f.filename).color }}>{fileType(f.filename).label}</span>
              <span className="l3-comment-chip__name">{displayName(f.filename)}</span>
              <button type="button" aria-label={`Remove ${displayName(f.filename)}`} onClick={() => setRemovedIds(p => [...p, f.id])}><FiX size={11} /></button>
            </span>
          ))}
          {files.map((f, i) => (
            <span key={`new-${i}-${f.name}`} className="l3-comment-chip l3-comment-chip--new">
              <span className="l3-comment-chip__type" style={{ background: fileType(f.name).color }}>{fileType(f.name).label}</span>
              <span className="l3-comment-chip__name">{f.name}</span>
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(p => p.filter((_, j) => j !== i))}><FiX size={11} /></button>
            </span>
          ))}
        </div>
      )}
      {fileErrors.length > 0 && (
        <div className="l3-comment-file-errors" role="alert">
          {fileErrors.map((m, i) => <div key={i}>{m}</div>)}
        </div>
      )}
      <div className="l3-comment-send-row">
        <span className="l3-comment-tools">
          <AppTooltip title={`Attach files (${ATTACHMENT_EXTENSIONS.join(', ')} · ${ATTACHMENT_MAX_MB}MB · max ${COMMENT_MAX_FILES})`}>
            <button type="button" className="l3-comment-attach" onClick={() => fileRef.current?.click()} aria-label="Attach files"><FiPaperclip size={13} /></button>
          </AppTooltip>
          <input ref={fileRef} type="file" multiple hidden accept={ATTACHMENT_EXTENSIONS.map(x => `.${x}`).join(',')}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <span className="l3-comment-hint">@ to mention · paste or drop files</span>
        </span>
        <span className="l3-comment-actions">
          {onCancel && <button className="cr-btn cr-btn--sm" onClick={onCancel} disabled={sending}>Cancel</button>}
          <button className="cr-btn cr-btn--primary cr-btn--sm" onClick={send} disabled={!canSend}>{sending ? 'Saving…' : submitLabel}</button>
        </span>
      </div>
    </div>
  );
};

// Files on a comment: thumbnails (image / PDF first page / CSV rows) that open
// the full-screen viewer.
const CommentFiles = ({ files, onOpen }) => (
  <div className="l3-comment-files">
    {files.map(f => (
      <button key={f.id} type="button" className="l3-comment-file" onClick={() => onOpen(f)}>
        <span className="l3-comment-file__thumb">
          <FileThumb name={f.filename} url={f.url} getUrl={() => sprintApi.commentFileUrl(f.comment_id, f.id)} cacheKey={`cmt-${f.id}`} />
        </span>
        <TitleTooltip title={displayName(f.filename)}><span className="l3-comment-file__name">{displayName(f.filename)}</span></TitleTooltip>
      </button>
    ))}
  </div>
);

export default SprintDashboard;
